import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Upload, FileSpreadsheet, CheckCircle, Play, Terminal, ChevronRight, X, ExternalLink, RefreshCcw, Pause, SkipForward, StopCircle, PlayCircle, Sparkles, Zap, Download } from 'lucide-react';
import { toast } from 'sonner';
import { parseExcelFile, validateExcelData, parseIncExcel, validateIncData } from '../lib/excelParser';
import { downloadInputSample, downloadIncSample } from '../lib/sampleTemplates';
import { matchAllData } from '../lib/DataProcessor.service';
import { buildIncLookup, getOrderedProperties } from '../lib/promptGenerator';
import { getSpecExtractModel, getClassificationModel, getExtractionEngine, getGeminiExtractionModel, getConditionGenerationModel, getPipelineStepEnabled, getAgentSettings, getSearchWithDescription } from '../lib/aiSettingsUtils';
import { useIndexedDB } from '../hooks/useIndexedDB';
import { apiClient, setApiTraceRecorder } from '../lib/api';
import { injectManufacturerMeta } from '../lib/enrichmentUtils';
import { combineDescriptions, getDescriptions, withDescriptions } from '../lib/descriptions';
import { readRankResult, labelScrapeUrl, describeMatch } from '../lib/rankPlan';
import { removePartNumberAndNounModifier } from '../lib/extractedDataCleaner';
import { describeConditionMemory } from '../lib/conditionLog';
import ProcessingOverlay from '../components/ProcessingOverlay';
import '../components/ProcessingOverlay.css';

function UploadPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { addItems, updateItem, addItem, addIncData, getAllIncData, clearIncData } = useIndexedDB();

    const [files, setFiles] = useState({ input: null, inc: null });
    const [dragActive, setDragActive] = useState(false);
    const [rescrapeData, setRescrapeData] = useState(null);
    // ── Processing Log sidebar state ──────────────────────────
    const [logSidebarItem, setLogSidebarItem] = useState(null);

    // ── Prompt Editor state ──────────────────────────────────
    const [editedPrompt, setEditedPrompt] = useState(null);

    /**
     * Helper to load Active BU Part A values for pipeline
     */
    // Enrichment pipeline state
    const [enrichmentRunning, setEnrichmentRunning] = useState(false);
    const [enrichmentLogs, setEnrichmentLogs] = useState([]);
    const [enrichmentStats, setEnrichmentStats] = useState({ total: 0, completed: 0, failed: 0, current: 0 });
    const consoleEndRef = useRef(null);

    // Step-by-step approval state
    const [stepReview, setStepReview] = useState(null); // { stepName, stepNumber, data, row }

    // ── Pipeline Control Refs (useRef so they're always current inside async loop) ──
    const pauseRef = useRef(false);
    const stopRef = useRef(false);
    const skipCurrentRef = useRef(false);
    const [pipelineState, setPipelineState] = useState('idle'); // 'idle' | 'running' | 'paused' | 'stopped' | 'completed'
    const approvalResolveRef = useRef(null);
    const pipelineStartTimeRef = useRef(null);
    const [currentStepName, setCurrentStepName] = useState('');

    // ── Processing mode ────────────────────────────────────────────────
    // 'enrichment' runs the full pipeline. 'cleansed' assumes the input is
    // already clean, so it goes Input + INC -> Step 0 (DB check) -> Step 6,
    // skipping search, classification, scraping and spec extraction. With no
    // scraped or spec content, Step 6 falls through to the row description,
    // which is what the input mapping provides.
    const PROCESSING_MODE_KEY = 'banyan_processing_mode';
    const CLEANSED_SKIPPED_STEPS = new Set(['step1', 'step1_5', 'step2', 'step2_5', 'step3', 'step3_5', 'step4', 'step4_5']);
    const [processingMode, setProcessingMode] = useState(() => {
        const stored = localStorage.getItem(PROCESSING_MODE_KEY);
        return stored === 'cleansed' ? 'cleansed' : 'enrichment';
    });
    const processingModeRef = useRef(processingMode);

    useEffect(() => {
        processingModeRef.current = processingMode;
        try {
            localStorage.setItem(PROCESSING_MODE_KEY, processingMode);
        } catch {
            // A blocked localStorage only costs the remembered choice.
        }
    }, [processingMode]);

    // The pipeline reads step gates through this, so the mode can switch steps
    // off without touching the user's own settings.
    const isStepEnabled = useCallback((stepId) => {
        if (processingModeRef.current === 'cleansed' && CLEANSED_SKIPPED_STEPS.has(stepId)) {
            return false;
        }
        return getPipelineStepEnabled(stepId);
    }, []);

    // Preview mode: read from localStorage, listen for TopBar toggle changes
    const PREVIEW_KEY = 'banyan_preview_mode';
    const previewModeRef = useRef(() => {
        const stored = localStorage.getItem(PREVIEW_KEY);
        return stored === null ? true : stored === 'true';
    });
    const [previewMode, setPreviewMode] = useState(previewModeRef.current);

    useEffect(() => {
        const onPreviewChange = (e) => {
            previewModeRef.current = e.detail;
            setPreviewMode(e.detail);
        };
        window.addEventListener('previewModeChanged', onPreviewChange);
        return () => window.removeEventListener('previewModeChanged', onPreviewChange);
    }, []);

    // Fetch active BU type Part A values
    const fetchActiveBUPartAValues = async () => {
        try {
            const data = await apiClient.getActiveBUPartAValues();
            if (data.success && data.data?.valuesA) {
                return data.data.valuesA.map(v => v.value_text).filter(Boolean);
            }
        } catch (error) {
            console.error('Error fetching active BU type:', error);
        }
        return [];
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e, fileType) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileChange(e.dataTransfer.files[0], fileType);
        }
    };

    const handleFileChange = (file, fileType) => {
        if (!file) return;
        const validExtensions = ['.xls', '.xlsx'];
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!validExtensions.includes(fileExtension)) {
            toast.error('Please upload a valid Excel file (.xls or .xlsx)');
            return;
        }
        setFiles(prev => ({ ...prev, [fileType]: file }));
        toast.success(`${fileType === 'input' ? 'Input Data' : 'INC Data'} Excel file selected`);
    };

    // ── Console Log Helpers ──────────────────────────────────────────
    const addLog = useCallback((message, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
        setEnrichmentLogs(prev => [...prev, { timestamp, message, type }]);
    }, []);

    useEffect(() => {
        consoleEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [enrichmentLogs]);

    // ── Approval Gate ────────────────────────────────────────────────
    const waitForApproval = (stepName, stepNumber, data, row) => {
        // If Preview is OFF → auto-approve immediately (no manual review)
        const isPreviewOn = localStorage.getItem(PREVIEW_KEY);
        if (isPreviewOn === 'false') {
            addLog(`  ⚡ Auto-approved: ${stepName}`, 'detail');
            return Promise.resolve('approve');
        }
        return new Promise((resolve) => {
            approvalResolveRef.current = resolve;
            setStepReview({ stepName, stepNumber, data, row });
        });
    };

    const handleApprove = () => {
        if (approvalResolveRef.current) {
            approvalResolveRef.current(editedPrompt !== null ? editedPrompt : 'approve');
            approvalResolveRef.current = null;
            setStepReview(null);
            setEditedPrompt(null);
        }
    };

    const handleSkipRow = () => {
        if (approvalResolveRef.current) {
            approvalResolveRef.current('skip');
            approvalResolveRef.current = null;
            setStepReview(null);
            setEditedPrompt(null);
        }
    };

    // ── Pipeline Control Handlers ────────────────────────────────────
    const handlePause = () => {
        pauseRef.current = true;
        setPipelineState('paused');
        addLog('⏸ Pipeline paused by user.', 'warning');
        toast.info('Pipeline paused');
    };

    const handleResume = () => {
        pauseRef.current = false;
        setPipelineState('running');
        addLog('▶ Pipeline resumed.', 'success');
        toast.success('Pipeline resumed');
    };

    const handleStop = () => {
        stopRef.current = true;
        pauseRef.current = false; // unblock if paused
        setPipelineState('stopped');
        addLog('🛑 Stop requested — finishing current record...', 'warning');
        toast.warning('Pipeline stopping after current record...');
        // If waiting for approval, auto-approve to unblock
        if (approvalResolveRef.current) {
            approvalResolveRef.current('approve');
            approvalResolveRef.current = null;
            setStepReview(null);
            setEditedPrompt(null);
        }
    };

    const handleSkipCurrent = () => {
        skipCurrentRef.current = true;
        addLog('⏭ Skip requested — moving to next record...', 'info');
        toast.info('Skipping current record...');
        // If waiting for approval, skip to unblock
        if (approvalResolveRef.current) {
            approvalResolveRef.current('skip');
            approvalResolveRef.current = null;
            setStepReview(null);
            setEditedPrompt(null);
        }
    };

    // Helper: check pipeline controls at loop top (returns 'continue', 'break', or 'ok')
    const checkPipelineControls = async (addLogFn) => {
        // Check STOP
        if (stopRef.current) {
            addLogFn('🛑 Pipeline stopped by user. All completed records are preserved.', 'warning');
            return 'break';
        }
        // Check PAUSE — spin-wait until resumed or stopped
        while (pauseRef.current && !stopRef.current) {
            await new Promise(r => setTimeout(r, 300));
        }
        if (stopRef.current) {
            addLogFn('🛑 Pipeline stopped by user. All completed records are preserved.', 'warning');
            return 'break';
        }
        // Check SKIP
        if (skipCurrentRef.current) {
            skipCurrentRef.current = false;
            addLogFn('  ⏭ Row skipped by user control', 'info');
            return 'continue';
        }
        return 'ok';
    };

    // Helper: mid-step control check (called BETWEEN steps within a single record)
    // Throws special errors so the record's try/catch can handle pause/stop/skip immediately
    const PIPELINE_STOP = '__PIPELINE_STOP__';
    const PIPELINE_SKIP = '__PIPELINE_SKIP__';
    const checkMidStep = async () => {
        // STOP — throw to exit current record immediately
        if (stopRef.current) {
            throw new Error(PIPELINE_STOP);
        }
        // PAUSE — spin-wait until resumed or stopped
        while (pauseRef.current && !stopRef.current) {
            await new Promise(r => setTimeout(r, 300));
        }
        if (stopRef.current) {
            throw new Error(PIPELINE_STOP);
        }
        // SKIP — throw to skip current record
        if (skipCurrentRef.current) {
            skipCurrentRef.current = false;
            throw new Error(PIPELINE_SKIP);
        }
    };
    // ── Detect rescrape data from ResultsPage navigation ────────────
    const autoStartPending = useRef(false);

    useEffect(() => {
        const state = location.state;
        if (state?.rescrapeKey) {
            try {
                const raw = sessionStorage.getItem(state.rescrapeKey);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    setRescrapeData(parsed);
                    sessionStorage.removeItem(state.rescrapeKey);
                    toast.info(`${parsed.length} item(s) loaded for rescraping`);
                    // If autoStart flag is set, mark pending
                    if (state.autoStart) {
                        autoStartPending.current = true;
                    }
                }
            } catch (e) {
                console.error('Failed to load rescrape data', e);
            }
        }
        // Support direct items from ScrapCart re-scrap
        if (state?.reScrapItems && Array.isArray(state.reScrapItems) && state.reScrapItems.length > 0) {
            setRescrapeData(state.reScrapItems);
            toast.info(`${state.reScrapItems.length} item(s) loaded from Scrap Cart for re-scraping`);
            if (state.autoStart) {
                autoStartPending.current = true;
            }
        }
    }, [location.state]);

    // Auto-start rescrape when data is loaded and autoStart flag is pending
    useEffect(() => {
        if (autoStartPending.current && rescrapeData && rescrapeData.length > 0 && !enrichmentRunning) {
            autoStartPending.current = false;
            // Small delay to let UI render first
            const timer = setTimeout(() => {
                startRescrapeProcess();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [rescrapeData, enrichmentRunning]);

    // ── Rescrape Pipeline (reuses same logic as main pipeline) ────────
    const startRescrapeProcess = async () => {
        if (!rescrapeData || rescrapeData.length === 0) {
            toast.error('No items to rescrape');
            return;
        }

        setEnrichmentRunning(true);
        setEnrichmentLogs([]);
        setEnrichmentStats({ total: 0, completed: 0, failed: 0, current: 0 });
        // Reset pipeline control refs
        pauseRef.current = false;
        stopRef.current = false;
        skipCurrentRef.current = false;
        setPipelineState('running');
        pipelineStartTimeRef.current = Date.now();
        setCurrentStepName('');
        const enrichmentResults = [];

        try {
            addLog(`🔄 Starting Rescrape Pipeline for ${rescrapeData.length} item(s)...`, 'header');
            addLog('─'.repeat(60), 'divider');

            let buPartAValues = [];
            try {
                buPartAValues = await fetchActiveBUPartAValues();
            } catch (e) {
                addLog('⚠️ Could not fetch BU Part A values, continuing with defaults', 'warning');
            }

            // Build enrichable rows from rescrape data
            const enrichableRows = rescrapeData.map(item => withDescriptions({
                ...item,
                rowNumber: item.rowNumber || item.id,
                givenSupplierName: item.givenSupplierName || '',
                givenSupplierPartnumber: item.givenSupplierPartnumber || '',
                manufacturerName: item.manufacturerName || '',
                manufacturerNumber: item.manufacturerNumber || '',
                nounModifier: item.nounModifier || '',
            }));

            addLog(`📋 ${enrichableRows.length} item(s) to rescrape`, 'info');

            // ── Noun/Modifier DB Resolution (Rescrape — strict) ──
            addLog('🔍 Resolving Noun/Modifier from DB...', 'step');
            try {
                const allPartNumbers = [];
                for (const row of enrichableRows) {
                    if (row.manufacturerNumber?.trim()) allPartNumbers.push(row.manufacturerNumber.trim());
                    if (row.givenSupplierPartnumber?.trim()) allPartNumbers.push(row.givenSupplierPartnumber.trim());
                    if (row.supplierPartNumber2?.trim()) allPartNumbers.push(row.supplierPartNumber2.trim());
                    if (row.supplierPartNumber3?.trim()) allPartNumbers.push(row.supplierPartNumber3.trim());
                    if (row.supplierPartNumber4?.trim()) allPartNumbers.push(row.supplierPartNumber4.trim());
                }
                console.log('[NM-DEBUG] allPartNumbers to lookup:', JSON.stringify(allPartNumbers));
                addLog(`  📊 Looking up ${allPartNumbers.length} part number(s): ${allPartNumbers.join(', ')}`, 'info');
                
                const nmResponse = allPartNumbers.length > 0
                    ? await apiClient.nounModifierBatchLookup(allPartNumbers)
                    : { map: {} };
                console.log('[NM-DEBUG] Full API response:', JSON.stringify(nmResponse));
                
                const nmMap = nmResponse.map || {};
                console.log('[NM-DEBUG] nmMap keys:', Object.keys(nmMap));
                addLog(`  📊 API returned ${Object.keys(nmMap).length} match(es): ${JSON.stringify(Object.keys(nmMap))}`, 'info');
                
                let dbMatched = 0, pending = 0;
                for (const row of enrichableRows) {
                    const keysToTry = [
                        row.manufacturerNumber?.trim(),
                        row.givenSupplierPartnumber?.trim(),
                        row.supplierPartNumber2?.trim(),
                        row.supplierPartNumber3?.trim(),
                        row.supplierPartNumber4?.trim(),
                    ].filter(Boolean);
                    console.log('[NM-DEBUG] Row keys to try:', keysToTry, '→ uppercase:', keysToTry.map(k => k.toUpperCase()));
                    let found = false;
                    for (const k of keysToTry) {
                        const uk = k.toUpperCase();
                        console.log('[NM-DEBUG] Checking nmMap for:', uk, '→ exists?', !!nmMap[uk], '→ value:', nmMap[uk]);
                        if (nmMap[uk]) { row.nounModifier = nmMap[uk].nounModifier; row._nmPending = false; found = true; dbMatched++; break; }
                    }
                    if (!found) {
                        if (row.nounModifier && row.nounModifier.trim()) {
                            row._nmPending = false;
                            dbMatched++;
                            console.log('[NM-DEBUG] Row has existing nounModifier:', row.nounModifier);
                        } else {
                            row.nounModifier = ''; row._nmPending = true; pending++;
                            console.log('[NM-DEBUG] Row marked as NM_PENDING — no match found');
                        }
                    }
                }
                addLog(`  ✅ Noun/Modifier: ${dbMatched} resolved from DB`, 'success');
                if (pending > 0) addLog(`  ⚠ ${pending} row(s) missing — marked as "Noun/Modifier Pending"`, 'warning');
            } catch (nmErr) {
                console.error('[NM-DEBUG] Lookup failed:', nmErr);
                addLog(`  ❌ Noun/Modifier lookup failed: ${nmErr.message}`, 'error');
                for (const row of enrichableRows) { row.nounModifier = ''; row._nmPending = true; }
            }

            setEnrichmentStats(prev => ({ ...prev, total: enrichableRows.length }));

            // Build INC lookup for dynamic property resolution per-row
            // For NM_PENDING items being retried, they have no promptProperties,
            // so we MUST load the INC file to resolve the schema
            let incLookup = new Map();
            const hasNmPendingItems = enrichableRows.some(r => r.reviewStatus === 'NM_PENDING' || r.status === 'nm_pending');
            if (files.inc) {
                // User uploaded an INC file on this page — use it
                try {
                    addLog('📂 Parsing INC Data Excel for schema resolution...', 'info');
                    const incDataParsed = await parseIncExcel(files.inc);
                    incLookup = buildIncLookup(incDataParsed);
                    addLog(`  ✅ Loaded ${incDataParsed.length} INC row(s) — ${incLookup.size} unique NM schemas`, 'success');
                    // Also persist for future rescrape
                    try { await clearIncData(); await addIncData(incDataParsed); } catch (_) {}
                } catch (incErr) {
                    addLog(`  ⚠ Failed to parse INC file: ${incErr.message}`, 'warning');
                }
            } else {
                // No INC file on page — try loading from IndexedDB (saved from previous upload)
                try {
                    const savedIncData = await getAllIncData();
                    if (savedIncData && savedIncData.length > 0) {
                        incLookup = buildIncLookup(savedIncData);
                        addLog(`📂 Loaded ${savedIncData.length} INC row(s) from saved data — ${incLookup.size} unique NM schemas`, 'success');
                    } else if (hasNmPendingItems) {
                        addLog('  ⚠ No INC data found (not uploaded previously). NM_PENDING items may fail at schema resolution.', 'warning');
                    }
                } catch (incErr) {
                    if (hasNmPendingItems) {
                        addLog(`  ⚠ Failed to load saved INC data: ${incErr.message}`, 'warning');
                    }
                }
            }

            // Reuse the exact same for-loop pipeline as startEnrichmentProcess
            for (let i = 0; i < enrichableRows.length; i++) {
                const row = enrichableRows[i];
                setApiTraceRecorder(null);
                const rowNum = row.rowNumber || (i + 1);
                const splName = (row.givenSupplierName || '').trim();
                const mfrName = (row.manufacturerName || '').trim();
                const companyName = splName || mfrName || '';
                const mfrPN = (row.manufacturerNumber || '').trim();
                const splPN = (row.givenSupplierPartnumber || '').trim();
                const identifier = (row.partNumber || '').trim() || mfrPN || splPN || '(description-only)';
                const { description1, description2 } = getDescriptions(row);
                const description = combineDescriptions({ description1, description2 });

                setEnrichmentStats(prev => ({ ...prev, current: i + 1 }));

                // ── Pipeline Control Checks ──
                const controlResult = await checkPipelineControls(addLog);
                if (controlResult === 'break') break;
                if (controlResult === 'continue') {
                    setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                    continue;
                }

                addLog(`\n▶ Rescrape ${i + 1}/${enrichableRows.length}: ${identifier}`, 'header');

                // ── Noun/Modifier Pending Gate ──────────────────────
                const hasAnyPN = !!(mfrPN || splPN);
                if (row._nmPending && hasAnyPN) {
                    addLog(`  ⏸ SKIPPED — Noun/Modifier not found in DB. Add it via Noun/Modifier Master page.`, 'warning');
                    if (row.id) {
                        try { await updateItem(row.id, { reviewStatus: 'NM_PENDING', status: 'nm_pending', pipelineStep: 'noun-modifier-pending' }); } catch (_) { }
                    }
                    setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                    continue;
                }

                // ── Dynamic Schema Resolution (rescrape uses saved properties or INC lookup) ──
                const attributeLines = row.promptProperties?.length > 0
                    ? [...new Map(row.promptProperties
                        .map(property => String(property || '').trim())
                        .filter(Boolean)
                        .map(property => [property.toUpperCase().replace(/\s+/g, ' '), property])).values()]
                    : getOrderedProperties(row.nounModifier, incLookup);

                if (attributeLines.length === 0) {
                    addLog(`  ❌ SKIPPED — No schema properties for NM "${row.nounModifier || '(empty)'}"`, 'error');
                    if (row.id) {
                        try { await updateItem(row.id, { reviewStatus: 'SCHEMA_MISSING', status: 'schema_missing', pipelineStep: 'schema-missing' }); } catch (_) { }
                    }
                    setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                    continue;
                }

                // ── Restart from scratch for previously NM_PENDING items ──
                // If this item was stuck at NM_PENDING before but NM is now resolved,
                // clear old pipeline data so it gets a fresh search + scrape
                const wasPreviouslyNmPending = (row.reviewStatus === 'NM_PENDING' || row.status === 'nm_pending');
                if (wasPreviouslyNmPending) {
                    addLog(`  🔄 Previously NM_PENDING — restarting pipeline from scratch`, 'info');
                    row.sourceUrl = null;
                    row.scrapedContent = null;
                    row.searchTier = 0;
                    row.classifiedManufacturers = [];
                    row.classifiedVendors = [];
                    row.resumeFromHold = false;
                }

                // Variables that persist through the pipeline
                let searchResults = null;
                let bestUrl = row.sourceUrl || null;
                let secondaryUrl = null; // vendor link — also scraped only when manufacturer confidence is 90% or lower
                let fallbackUrl = null; // link not chosen for scraping — used if the 1st link is only a homepage
                let scrapePlanInfo = null; // Step 2 scrape plan, for Step 3 logs
                let urlScore = null;
                let scrapedContent = row.scrapedContent || null; // Restore content if resuming from hold
                let extractedAttrs = null;
                let mappedAttrs = null;
                let classifiedMfrs = row.classifiedManufacturers || [];
                let classifiedVendors = row.classifiedVendors || [];
                let rowSkipped = false;
                let skipReason = null; // 'user' or 'auto' — tracks why rowSkipped was set
                let searchTier = row.searchTier || 0;
                let descriptionOnlyMode = false;
                let dataSource = 'Vendor URL';
                let step2MfrName = '';  // manufacturer name from Step 2 classification
                let step2MfrPN = '';    // manufacturer part number from Step 2
                const isResumingHold = !!row.resumeFromHold;

                // Structured per-row processing log
                const rowLog = [];
                const logEntry = (stage, action, result, data = {}) => {
                    rowLog.push({ stage, action, result, data, timestamp: new Date().toISOString() });
                };
                // Every API call from here on is recorded in this row's processing log
                setApiTraceRecorder(entry => rowLog.push(entry));
                logEntry('INIT', isResumingHold ? 'Resuming from HOLD state' : 'Rescrape started', 'info', {
                    identifier, companyName, description,
                    supplierPN: splPN, manufacturerPN: mfrPN
                });

                // ── Incremental IndexedDB save: update existing record to processing ──
                const dbId = row.id || null;
                if (dbId) {
                    try {
                        await updateItem(dbId, { status: 'processing', pipelineStep: 'init', reviewStatus: 'PROCESSING', processingLog: rowLog });
                    } catch (dbErr) {
                        console.error('IndexedDB init update failed:', dbErr);
                    }
                }

                // Helper: save stage data to IndexedDB (non-blocking)
                const saveStage = async (stage, data) => {
                    if (!dbId) return;
                    try {
                        await updateItem(dbId, { ...data, pipelineStep: stage, processingLog: rowLog });
                    } catch (e) {
                        console.error(`IndexedDB stage save failed (${stage}):`, e);
                    }
                };

                try {
                    let holdMode = false;
                    let specContent = null;
                    const recordStartTime = performance.now();
                    const stepTimer = { _start: 0 };
                    stepTimer.start = () => { stepTimer._start = performance.now(); };
                    stepTimer.log = (label) => {
                        const elapsed = ((performance.now() - stepTimer._start) / 1000).toFixed(1);
                        addLog(`  ⏱ ${label} — ${elapsed}s`, 'info');
                    };

                    if (isResumingHold) {
                        addLog(`  ⏩ Resuming from Hold — reviewing scraped content...`, 'warning');
                        if (!scrapedContent) {
                            addLog(`  ❌ No scraped content available to resume`, 'error');
                            rowSkipped = true; skipReason = 'auto';
                        } else {
                            // Show the held scraped content for review/edit before proceeding
                            const alphaCount = (scrapedContent.match(/[A-Za-z0-9]/g) || []).length;
                            const holdDecision = await waitForApproval(
                                'Scraped Content (Held)', 3,
                                {
                                    type: 'scraped_content',
                                    url: bestUrl || 'N/A',
                                    contentLength: scrapedContent.length,
                                    filteredCharCount: alphaCount,
                                    preview: scrapedContent
                                },
                                { rowNum, identifier }
                            );
                            if (holdDecision === 'skip') {
                                addLog(`  ⏭ Row skipped by user`, 'info');
                                rowSkipped = true; skipReason = 'user';
                            } else {
                                // If user edited the scraped content, use their version
                                if (typeof holdDecision === 'string' && holdDecision !== 'approve') {
                                    scrapedContent = holdDecision;
                                    addLog(`  ✏️ Content edited by user (${scrapedContent.length} chars)`, 'detail');
                                }
                                addLog(`  ✅ Scraped content approved (${scrapedContent.length} chars)`, 'success');
                            }
                        }
                    } else {
                        // ═══ STEP 0: DB CHECK ═══════════════════════════════════
                        const checkPN = (mfrPN || splPN || '').trim();
                        if (checkPN && getPipelineStepEnabled('step0')) {
                            stepTimer.start();
                            addLog(`  🗃️ Step 0: Checking Master DB for PN "${checkPN}"...`, 'step');
                            try {
                                const dbCheck = await apiClient.checkPartNumber(checkPN);
                                if (dbCheck.success && dbCheck.found) {
                                    addLog(`  ✅ Found in Master DB! (matched: ${dbCheck.matchedColumn})`, 'success');
                                    logEntry('DB_CHECK', `PN found in ${dbCheck.matchedColumn}`, 'match', { matchedColumn: dbCheck.matchedColumn });

                                    const dbDecision = await waitForApproval(
                                        'Existing Data Found in Master DB',
                                        0,
                                        {
                                            type: 'db_match',
                                            partNumber: checkPN,
                                            matchedColumn: dbCheck.matchedColumn,
                                            data: dbCheck.data
                                        },
                                        { rowNum, identifier }
                                    );

                                    if (dbDecision === 'approve') {
                                        addLog(`  ✅ Using existing data from Master DB — skipping pipeline`, 'success');
                                        logEntry('DB_CHECK', 'Reusing existing data', 'success');
                                        logEntry('COMPLETE', 'Pipeline completed (DB match)', 'success');
                                        // Merge: existing row data (Parent) + DB data (Child)
                                        // Parent values take priority; DB fills gaps
                                        let mergedData = dbCheck.data || {};
                                        if (row.chatgptOutput) {
                                            try {
                                                const existingAI = typeof row.chatgptOutput === 'string'
                                                    ? JSON.parse(row.chatgptOutput) : row.chatgptOutput;
                                                if (existingAI && typeof existingAI === 'object') {
                                                    const merged = { ...mergedData }; // Start with DB data
                                                    for (const [key, val] of Object.entries(existingAI)) {
                                                        // Existing (Parent) values override DB when non-empty
                                                        if (val !== null && val !== undefined && val !== '' && val !== 'null' && val !== 'NULL') {
                                                            merged[key] = val;
                                                        }
                                                    }
                                                    mergedData = merged;
                                                }
                                            } catch { /* keep DB data if parse fails */ }
                                        }
                                        await saveStage('complete', {
                                            sourceType: 'DB',
                                            chatgptOutput: JSON.stringify(mergedData),
                                            chatgptSuccess: true,
                                            chatgptRaw: JSON.stringify(dbCheck.data),
                                            chatgptError: null,
                                            reviewStatus: 'PENDING',
                                            status: 'completed',
                                            sourceUrl: null,
                                            searchTier: 0,
                                            processingLog: rowLog,
                                        });
                                        setEnrichmentStats(prev => ({ ...prev, completed: prev.completed + 1 }));
                                        addLog(`  ✅ Row complete! (DB match)`, 'success');
                                        addLog('─'.repeat(60), 'divider');
                                        continue;
                                    } else {
                                        addLog(`  ▶️ User chose to re-scrape — proceeding with pipeline`, 'info');
                                        logEntry('DB_CHECK', 'User opted to re-scrape', 'info');
                                    }
                                } else {
                                    addLog(`  ℹ️ Not found in Master DB — proceeding with search`, 'info');
                                }
                            } catch (dbErr) {
                                addLog(`  ⚠️ DB check failed: ${dbErr.message} — proceeding with search`, 'warning');
                            }
                        } else if (checkPN && !getPipelineStepEnabled('step0')) {
                            addLog(`  ⏭️ Step 0: DB Check — SKIPPED (disabled in settings)`, 'warning');
                        }
                        stepTimer.log('Step 0: DB Check');

                        await checkMidStep(); // Pause/Stop check between Step 0 → Step 1
                        // ═══ STEP 1: SUPPLIER-VALIDATED SEARCH ═══════════════════
                        if (!getPipelineStepEnabled('step1')) {
                            addLog(`  ⏭️ Step 1: Supplier Search — SKIPPED (disabled in settings)`, 'warning');
                        }
                        if (getPipelineStepEnabled('step1')) {
                        stepTimer.start();
                        // Build supplier-PN pairs for domain validation
                        const supplierPNPairs = [
                            { supplier: (row.givenSupplierName || '').trim(), partNumber: splPN },
                            { supplier: (row.givenSupplierName2 || '').trim(), partNumber: (row.supplierPartNumber2 || '').trim() },
                        ].filter(p => p.supplier && p.partNumber);

                        const allSearchPNs = [...new Set(supplierPNPairs.map(p => p.partNumber).concat(mfrPN ? [mfrPN] : []))];

                        // Use nounModifier as fallback description for search when description is empty
                        // Pipeline setting "Description in Serper search": when OFF, Serper gets the Part Number only
                        const descriptionSearchOn = getSearchWithDescription();
                        const searchDesc = descriptionSearchOn ? (description1 || (row.nounModifier || '').trim()) : '';
                        const hasDesc = !!searchDesc;
                        addLog(`  🔍 Step 1: Supplier-Validated Search (${allSearchPNs.length} PNs${hasDesc ? ' + Description' : descriptionSearchOn ? '' : ' only — Description search OFF'})...`, 'step');
                        if (supplierPNPairs.length > 0) addLog(`    ↳ Pairs: ${supplierPNPairs.map(p => `${p.supplier}→"${p.partNumber}"`).join(', ')}`, 'detail');
                        if (allSearchPNs.length > 1) addLog(`    ↳ OR query: ${allSearchPNs.map(p => `"${p}"`).join(' OR ')}`, 'detail');
                        if (!description && searchDesc) addLog(`    ↳ No description — using Noun/Modifier "${searchDesc}" as fallback`, 'detail');
                        if (!descriptionSearchOn) addLog(`    ↳ Description search is OFF (Pipeline settings) — Serper gets the Part Number only; AI still picks the Manufacturer and Vendor websites`, 'detail');
                        else if (!hasDesc) addLog(`    ⚠️ No description or Noun/Modifier available for description search`, 'warning');

                        const searchInput = {
                            description: searchDesc,
                            supplierPNPairs,
                            manufacturerPartNumber: mfrPN || undefined,
                        };

                        const step1Decision = await waitForApproval(
                            'Supplier-Validated Search (Rescrape)',
                            1,
                            {
                                type: 'search_validated_input',
                                supplierPNPairs,
                                manufacturerPartNumber: mfrPN || undefined,
                                description,
                                descriptionForSearch: searchDesc,
                                descriptionSearchOn,
                                companyName
                            },
                            { rowNum, identifier }
                        );

                        if (step1Decision === 'skip') {
                            addLog(`  ⏭ Row skipped by user`, 'info');
                            rowSkipped = true; skipReason = 'user';
                        }

                        if (!rowSkipped) {
                            addLog(`  🔍 Calling Serper API (supplier-validated search)...`, 'step');

                            let searchResp = supplierPNPairs.length > 0
                                ? await apiClient.stepSearchValidated(searchInput)
                                : await apiClient.stepSearch({ description: searchDesc, partNumbers: allSearchPNs, supplierPartNumber: splPN, manufacturerPartNumber: mfrPN });

                            const agentSettings = getAgentSettings();
                            if (searchResp.success && (!searchResp.results || searchResp.results.length === 0)
                                && agentSettings.enabled && agentSettings.triggerOnNoResults) {
                                addLog('  🤖 Agentic AI: normal search found no URLs; planning alternate searches...', 'step');
                                logEntry('AGENT', 'Starting Agentic URL Recovery', 'info', { settings: agentSettings });
                                try {
                                    const recovery = await apiClient.agentRecoverUrls({
                                        supplierNames: supplierPNPairs.map(pair => pair.supplier),
                                        partNumbers: allSearchPNs,
                                        manufacturerPartNumber: mfrPN,
                                        description: searchDesc,
                                        nounModifier: descriptionSearchOn ? (row.nounModifier || '') : '',
                                        settings: agentSettings,
                                    });
                                    if (recovery.success && recovery.results?.length) {
                                        searchResp = {
                                            ...searchResp,
                                            results: recovery.results,
                                            totalResults: recovery.results.length,
                                            searchSources: { agentic: recovery.results.length },
                                            agentRecovery: recovery,
                                        };
                                        addLog(`  ✅ Agentic AI recovered ${recovery.results.length} candidate URL(s) using ${recovery.attemptedQueries.length} alternate query(s).`, 'success');
                                        logEntry('AGENT', 'Agentic URL Recovery found candidates', 'success', recovery);
                                    } else {
                                        addLog('  ⚠️ Agentic AI could not recover a URL. Using description-only extraction.', 'warning');
                                        logEntry('AGENT', 'Agentic URL Recovery exhausted', 'warning', recovery);
                                    }
                                } catch (agentError) {
                                    addLog(`  ⚠️ Agentic URL Recovery failed: ${agentError.message}. Using description-only extraction.`, 'warning');
                                    logEntry('AGENT', 'Agentic URL Recovery failed', 'error', { error: agentError.message });
                                }
                            }

                            if (!searchResp.success) {
                                addLog(`  ❌ Search failed: ${searchResp.error || 'Unknown error'}`, 'error');
                                logEntry('SEARCH', 'Search failed', 'error', { error: searchResp.error });
                                rowSkipped = true; skipReason = 'auto';
                            } else if (!searchResp.results || searchResp.results.length === 0) {
                                addLog(`  ⚠️ No search results found. Switching to AI description extraction.`, 'warning');
                                descriptionOnlyMode = true;
                                logEntry('SEARCH', 'No results found', 'warning');
                                await saveStage('search', { searchTier: 0, sourceType: 'Description' });
                            } else {
                                searchResults = searchResp.results;
                                const srcInfo = searchResp.searchSources || {};
                                const vReport = searchResp.validationReport;

                                addLog(`  ✅ Got ${searchResults.length} result(s) (PN: ${srcInfo.partNumber || 0}, Desc: ${srcInfo.description || 0})`, 'success');

                                logEntry('SEARCH', `Found ${searchResults.length} result(s)`, 'success', {
                                    searchSources: srcInfo,
                                    urls: searchResults.map(r => ({ title: r.title, url: r.link, snippet: r.snippet, domain: r.domain, searchSource: r.searchSource }))
                                });

                                // Show validation report for user approval
                                const step1bDecision = await waitForApproval(
                                    'Search Results + Validation (Rescrape)',
                                    1,
                                    {
                                        type: 'search_validated_results',
                                        results: searchResults,
                                        validationReport: vReport,
                                        searchSources: srcInfo,
                                        totalResults: searchResults.length
                                    },
                                    { rowNum, identifier }
                                );

                                stepTimer.log('Step 1: Search');
                                if (step1bDecision === 'skip') {
                                    addLog(`  ⏭ Row skipped by user`, 'info');
                                    rowSkipped = true; skipReason = 'user';
                                } else {
                                    await saveStage('search', { searchTier: 1, sourceType: 'Online', validationReport: vReport });

                                    await checkMidStep(); // Pause/Stop check between Step 1 → Step 1.5
                                    // ═══ STEP 1.5: DB MANUFACTURER PRIORITY ═══════════════
                                    let dbManufacturerInfo = null;
                                    if (!getPipelineStepEnabled('step1_5')) {
                                        addLog(`  ⏭️ Step 1.5: DB MFR Priority — SKIPPED (disabled in settings)`, 'warning');
                                    } else try {
                                        addLog(`  🏭 Step 1.5: Checking search results against DB manufacturers...`, 'step');
                                        const dbMatchResp = await apiClient.stepMatchDbManufacturers({ searchResults });
                                        if (dbMatchResp.success && dbMatchResp.matchCount > 0) {
                                            searchResults = dbMatchResp.prioritizedResults;
                                            // Capture first DB manufacturer match for Step 2
                                            const firstMatch = dbMatchResp.dbMatches[0];
                                            dbManufacturerInfo = {
                                                name: firstMatch.manufacturer,
                                                url: firstMatch.manufacturerUrl || ''
                                            };
                                            addLog(`  ✅ DB Manufacturer Priority: ${dbMatchResp.matchCount}/${dbMatchResp.totalResults} URLs matched DB manufacturers — moved to top`, 'success');
                                            for (const m of dbMatchResp.dbMatches) {
                                                addLog(`    ↳ ${m.manufacturer} (${m.matchType}) → ${m.domain || m.url}`, 'detail');
                                            }
                                            if (dbManufacturerInfo.url) {
                                                addLog(`    🔗 DB Manufacturer URL: ${dbManufacturerInfo.url} — will be passed to Step 2`, 'success');
                                            }
                                            logEntry('SEARCH', `DB manufacturer priority: ${dbMatchResp.matchCount} URLs boosted`, 'success', {
                                                matchCount: dbMatchResp.matchCount,
                                                dbMatches: dbMatchResp.dbMatches,
                                                dbManufacturerInfo
                                            });
                                        } else {
                                            addLog(`  ℹ️ No DB manufacturer matches found — proceeding with original order`, 'info');
                                        }
                                    } catch (dbMatchErr) {
                                        addLog(`  ⚠️ DB match check failed: ${dbMatchErr.message} — proceeding with original order`, 'warning');
                                    }

                                    // ═══ STEP 2: AI URL SELECTION ═══════════════════
                                    if (!getPipelineStepEnabled('step2')) {
                                        // Step 2 OFF → scrape the top search result (already DB-manufacturer-prioritised by Step 1.5)
                                        const topResult = searchResults?.[0];
                                        if (topResult?.link) {
                                            bestUrl = topResult.link;
                                            secondaryUrl = null;
                                            fallbackUrl = null;
                                            scrapePlanInfo = null;
                                            dataSource = 'Vendor URL';
                                            step2MfrName = dbManufacturerInfo?.name || '';
                                            step2MfrPN = mfrPN || splPN || '';
                                            addLog(`  ⏭️ Step 2: AI Link Ranking — SKIPPED (disabled in settings) → scraping top search result: ${bestUrl}`, 'warning');
                                            logEntry('AI_SELECT', 'Skipped (disabled in settings) — using top search result', 'skipped', { url: bestUrl, title: topResult.title || '', manufacturer: step2MfrName || null });
                                        } else {
                                            addLog(`  ⏭️ Step 2: AI Link Ranking — SKIPPED (disabled in settings), no search result to scrape`, 'warning');
                                        }
                                    } else {
                                    setCurrentStepName('Step 2: AI Link Ranking');
                                    // ─── Step 2a: Preview classification prompt ───
                                    const rankPayload = {
                                        urls: searchResults,
                                        description,
                                        manufacturerPartNumber: mfrPN,
                                        supplierPartNumber: splPN,
                                        classificationModel: getClassificationModel(),
                                        supplierNames: [splName, (row.givenSupplierName2 || '').trim()].filter(Boolean),
                                        dbManufacturer: dbManufacturerInfo,
                                        partNumbers: allSearchPNs, // exact part number match is checked against all of these
                                    };

                                    stepTimer.start();
                                    addLog(`  📝 Step 2: AI Link Ranking — manufacturer & vendor link with confidence...`, 'step');
                                    // ─── Step 2a: Preview classification prompt (ONLY if Preview ON) ───
                                    const isPreviewActive = localStorage.getItem(PREVIEW_KEY) !== 'false';
                                    if (isPreviewActive) {
                                        const previewResp = await apiClient.stepRankPreview(rankPayload);
                                        if (previewResp.success) {
                                            const previewDecision = await waitForApproval(
                                                'Link Ranking Prompt',
                                                2,
                                                {
                                                    type: 'classification_prompt_preview',
                                                    fullPrompt: previewResp.fullPrompt,
                                                    promptLength: previewResp.promptLength,
                                                    sourceCount: previewResp.sourceCount,
                                                    model: previewResp.model,
                                                    sources: previewResp.sources
                                                },
                                                { rowNum, identifier }
                                            );
                                            if (previewDecision === 'skip') { addLog(`  ⏭ Row skipped by user`, 'info'); rowSkipped = true; skipReason = 'user'; }
                                        }
                                    }

                                    // ─── Step 2b: Send to AI ───
                                    if (!rowSkipped) {
                                        addLog(`  🤖 Sending links to AI for ranking...`, 'step');
                                        const rankResp = await apiClient.stepRank(rankPayload);

                                        if (rankResp.success && rankResp.matchStatus === 'NO_MATCH_FOUND') {
                                            // Only links with an exact part number match may continue — exit with "No Match Found"
                                            const ranked = readRankResult(rankResp);
                                            classifiedMfrs = ranked.classifiedMfrs;
                                            classifiedVendors = ranked.classifiedVendors;
                                            ranked.logLines.forEach(([message, level]) => addLog(message, level));
                                            logEntry('AI_SELECT', ranked.summary, 'error', ranked.logData);
                                            rowSkipped = true; skipReason = 'no_match';
                                        } else if (!rankResp.success || (!rankResp.scrapable_url && !rankResp.manufacturer_url)) {
                                            addLog(`  ❌ No suitable URL found after AI selection`, 'error');
                                            rowSkipped = true; skipReason = 'auto';
                                        } else {
                                            const selMethod = rankResp.selectionMethod || 'AI_SELECTED';
                                            const step2Decision = await waitForApproval(
                                                'AI Link Ranking',
                                                2,
                                                {
                                                    type: 'url_selection',
                                                    scrapable_url: rankResp.scrapable_url,
                                                    manufacturer_url: rankResp.manufacturer_url || '',
                                                    vendor_urls: rankResp.vendor_urls || [],
                                                    manufacturers: rankResp.manufacturers || [],
                                                    vendors: rankResp.vendors || [],
                                                    selectionMethod: selMethod,
                                                    manufacturer: rankResp.manufacturer || null,
                                                    vendor: rankResp.vendor || null,
                                                    scrapePlan: rankResp.scrapePlan || null,
                                                    notes: rankResp.notes || [],
                                                    searchResults: searchResults.map(r => ({ title: r.title, link: r.link, snippet: r.snippet, position: r.position, domain: r.domain, searchSource: r.searchSource }))
                                                },
                                                { rowNum, identifier }
                                            );

                                            if (step2Decision === 'skip') {
                                                addLog(`  ⏭ Row skipped by user`, 'info');
                                                rowSkipped = true; skipReason = 'user';
                                            } else {
                                                // Step 2 result: manufacturer + vendor link with confidence, and the scrape plan
                                                // (manufacturer first; vendor too only if manufacturer confidence is 90% or lower)
                                                const ranked = readRankResult(rankResp);
                                                classifiedMfrs = ranked.classifiedMfrs;
                                                classifiedVendors = ranked.classifiedVendors;
                                                bestUrl = ranked.bestUrl;
                                                secondaryUrl = ranked.secondaryUrl;
                                                fallbackUrl = ranked.fallbackUrl;
                                                scrapePlanInfo = ranked.plan;
                                                dataSource = ranked.dataSource;
                                                ranked.logLines.forEach(([message, level]) => addLog(message, level));
                                                logEntry('AI_SELECT', ranked.summary, 'success', ranked.logData);
                                            }
                                            await saveStage('url_selection', { sourceUrl: bestUrl, secondaryUrl, selectionMethod: selMethod, classifiedManufacturers: classifiedMfrs, classifiedVendors: classifiedVendors, searchTier: 1 });
                                            // Capture manufacturer info from Step 2 for Step 2.5
                                            step2MfrName = classifiedMfrs[0]?.name || '';
                                            step2MfrPN = classifiedMfrs[0]?.partNumber || mfrPN || splPN || '';

                                            // Fallback: if AI didn't classify a manufacturer, use DB manufacturer from Step 1.5
                                            if (!step2MfrName && dbManufacturerInfo?.name) {
                                                step2MfrName = dbManufacturerInfo.name;
                                                step2MfrPN = mfrPN || splPN || '';
                                                addLog(`  ℹ️ AI didn't classify a manufacturer — using DB manufacturer "${step2MfrName}" from Step 1.5`, 'info');
                                                logEntry('AI_SELECT', `No AI manufacturer — fallback to DB manufacturer "${step2MfrName}"`, 'info', { dbManufacturerInfo });
                                            }
                                        }
                                    } // end: if (!rowSkipped) — Step 2b
                                    stepTimer.log('Step 2: Classification');
                                    } // end step2 enabled check
                                }
                            } // end: step1b else (validated results approved)
                        }
                        } // end step1 enabled check

                        await checkMidStep(); // Pause/Stop check between Step 2 → Step 2.5
                        // ═══ STEP 2.5: MANUFACTURER VALIDATION & ROUTING (Rescrape) ═══
                        if (!rowSkipped && !descriptionOnlyMode && bestUrl && step2MfrName && getPipelineStepEnabled('step2_5')) {
                            setCurrentStepName('Step 2.5: Manufacturer Validation');
                            addLog(`  🏭 Step 2.5: Manufacturer Validation...`, 'step');
                            try {
                                const valResp = await apiClient.stepManufacturerValidate({
                                    scrapableUrl: bestUrl,
                                    manufacturerName: step2MfrName,
                                    manufacturerPartNumber: step2MfrPN
                                });

                                if (valResp.success) {
                                    const step25Decision = await waitForApproval(
                                        'Manufacturer Validation', 2.5,
                                        {
                                            type: 'manufacturer_validation',
                                            scrapableUrl: bestUrl,
                                            manufacturerName: step2MfrName,
                                            partNumber: step2MfrPN,
                                            action: valResp.action,
                                            source: valResp.source,
                                            dataSource: valResp.dataSource,
                                            domainMatch: valResp.domainMatch,
                                            homepageDetected: valResp.homepageDetected || false,
                                            dbMatch: valResp.dbMatch,
                                            resultUrl: valResp.resultUrl,
                                            manufacturerUrl: valResp.manufacturerUrl,
                                            manufacturerInfo: valResp.manufacturerInfo,
                                            steps: valResp.steps
                                        },
                                        { rowNum, identifier }
                                    );

                                    if (step25Decision === 'skip') {
                                        addLog(`  ⏭ Row skipped by user`, 'info');
                                        rowSkipped = true; skipReason = 'user';
                                    } else {
                                        dataSource = valResp.dataSource || 'Vendor URL';
                                        if (valResp.action === 'REROUTE' && valResp.resultUrl) {
                                            addLog(`  🔄 Rerouting to manufacturer URL: ${valResp.resultUrl}`, 'success');
                                            bestUrl = valResp.resultUrl;
                                            dataSource = 'Manufacturer URL';
                                            logEntry('MFR_VALIDATE', `Rerouted to manufacturer URL`, 'success', {
                                                originalUrl: valResp.scrapableUrl, resultUrl: valResp.resultUrl,
                                                manufacturer: step2MfrName, source: valResp.source
                                            });
                                        } else if (valResp.action === 'HOMEPAGE_FALLBACK') {
                                            // 1st link is only a homepage → scrape the other ranked link instead
                                            // (even if it was skipped by the 90% rule, since the 1st link is unusable)
                                            if (fallbackUrl && fallbackUrl !== bestUrl) {
                                                const homepageUrl = bestUrl;
                                                const fallbackLink = [scrapePlanInfo?.secondary, scrapePlanInfo?.skipped].find(l => l?.url === fallbackUrl);
                                                addLog(`  ⚠️ 1st link is only a homepage (${homepageUrl}) — scraping the other ranked link instead: ${fallbackUrl} (${labelScrapeUrl(fallbackUrl, scrapePlanInfo)})`, 'warning');
                                                bestUrl = fallbackUrl;
                                                secondaryUrl = null;
                                                dataSource = fallbackLink?.role === 'manufacturer' ? 'Manufacturer URL' : 'Vendor URL';
                                                logEntry('MFR_VALIDATE', 'Homepage fallback: switched to the other ranked link', 'warning', {
                                                    homepageUrl, newUrl: bestUrl, newUrlRank: labelScrapeUrl(bestUrl, scrapePlanInfo),
                                                    manufacturer: step2MfrName
                                                });
                                            } else {
                                                addLog(`  ⚠️ Manufacturer URL is a homepage (${bestUrl}) — no other ranked link available, continuing with homepage`, 'warning');
                                                dataSource = 'Manufacturer URL';
                                                logEntry('MFR_VALIDATE', `Homepage detected, no other ranked link`, 'warning', {
                                                    homepageUrl: bestUrl, manufacturer: step2MfrName
                                                });
                                            }
                                        } else if (valResp.domainMatch) {
                                            addLog(`  ✅ URL already from manufacturer domain`, 'success');
                                            dataSource = 'Manufacturer URL';
                                            logEntry('MFR_VALIDATE', `Domain matches manufacturer`, 'success', { manufacturer: step2MfrName });
                                        } else {
                                            addLog(`  ℹ️ No manufacturer match in DB — using vendor URL`, 'info');
                                            dataSource = 'Vendor URL';
                                            logEntry('MFR_VALIDATE', `No manufacturer match`, 'info', { manufacturer: step2MfrName });
                                        }
                                        await saveStage('manufacturer_validate', { sourceUrl: bestUrl, secondaryUrl, dataSource });
                                    }
                                } else {
                                    addLog(`  ⚠️ Step 2.5 failed: ${valResp.error} — continuing`, 'warning');
                                }
                            } catch (valErr) {
                                addLog(`  ⚠️ Step 2.5 error: ${valErr.message} — continuing`, 'warning');
                            }
                        } else if (!rowSkipped && !descriptionOnlyMode && bestUrl && !step2MfrName) {
                            addLog(`  ℹ️ Step 2.5: Skipped — no manufacturer name from classification`, 'info');
                        }

                        // Description-only mode: will use description as content in Step 5 dynamic prompt
                        if (!rowSkipped && descriptionOnlyMode) {
                            addLog(`  ℹ️ Description-only mode — will proceed with dynamic prompt`, 'info');
                        }

                        // ═══ STEP 3: SCRAPE (Firecrawl) — Parallel ═══════════════
                        if (!rowSkipped && !descriptionOnlyMode && getPipelineStepEnabled('step3')) {
                            await checkMidStep(); // Pause/Stop check before Step 3
                            const urlsToScrape = [bestUrl, secondaryUrl].filter(Boolean);
                            addLog(`  📄 Step 3: Scraping ${urlsToScrape.length} URL(s) with Firecrawl${urlsToScrape.length > 1 ? ' (parallel)' : ''}...`, 'step');
                            // Scrape plan from Step 2: manufacturer first; vendor too only if manufacturer confidence is 90% or lower
                            const notScrapedLink = !secondaryUrl ? scrapePlanInfo?.skipped || null : null;
                            if (notScrapedLink && notScrapedLink.url !== bestUrl) {
                                addLog(`  ⏭ 2nd link not scraped: ${labelScrapeUrl(notScrapedLink.url, scrapePlanInfo)} — ${notScrapedLink.reason}`, 'detail');
                            }
                            logEntry('SCRAPE', urlsToScrape.length > 1 ? 'Scraping 1st and 2nd ranked links' : 'Scraping 1st ranked link only', 'info', {
                                firstUrl: bestUrl,
                                firstLink: labelScrapeUrl(bestUrl, scrapePlanInfo),
                                secondUrl: secondaryUrl || null,
                                secondLink: secondaryUrl ? labelScrapeUrl(secondaryUrl, scrapePlanInfo) : null,
                                notScraped: notScrapedLink && notScrapedLink.url !== bestUrl
                                    ? { url: notScrapedLink.url, role: notScrapedLink.role, confidence: notScrapedLink.confidence, reason: notScrapedLink.reason }
                                    : null,
                                rule: scrapePlanInfo?.rule || null,
                            });

                            let primaryContent = '';
                            let secondaryContent = '';
                            let anyHold = false;

                            // ── Fire all scrapes in parallel ──
                            const scrapeStartTime = performance.now();
                            addLog(`  📤 Step 3a: Sending 1st link to Firecrawl — ${labelScrapeUrl(urlsToScrape[0], scrapePlanInfo)}...`, 'step');
                            addLog(`    ↳ ${urlsToScrape[0]}`, 'detail');

                            const deepClean = getPipelineStepEnabled('step3_5');
                            if (!deepClean) addLog(`  ⏭️ Step 3.5: Content Deep Clean — SKIPPED (disabled in settings)`, 'warning');
                            const scrapePromises = [apiClient.stepScrape({ url: urlsToScrape[0], deepClean })];
                            if (urlsToScrape.length > 1) {
                                addLog(`  📤 Step 3b: Sending 2nd link to Firecrawl (parallel) — ${labelScrapeUrl(urlsToScrape[1], scrapePlanInfo)}...`, 'step');
                                addLog(`    ↳ ${urlsToScrape[1]}`, 'detail');
                                scrapePromises.push(apiClient.stepScrape({ url: urlsToScrape[1], deepClean }));
                            }

                            const scrapeResults = await Promise.allSettled(scrapePromises);
                            const scrapeElapsed = ((performance.now() - scrapeStartTime) / 1000).toFixed(1);
                            addLog(`  ⏱ Scraping completed in ${scrapeElapsed}s`, 'info');

                            // ── Process primary result ──
                            const primarySettled = scrapeResults[0];
                            if (primarySettled.status === 'fulfilled' && primarySettled.value.success) {
                                const primaryResult = primarySettled.value;
                                if (primaryResult.holdRequired) {
                                    addLog(`  ⏸ Primary content exceeds limit (${primaryResult.filteredCharCount}/${primaryResult.charLimit} chars) → HOLD`, 'warning');
                                    primaryContent = primaryResult.cleanedContent;
                                    anyHold = true;
                                } else {
                                    primaryContent = primaryResult.cleanedContent;
                                    addLog(`  ✅ Primary scraped ${primaryContent.length} chars from ${urlsToScrape[0]}`, 'success');
                                    if (primaryResult.deepCleanStats) {
                                        const ds = primaryResult.deepCleanStats;
                                        addLog(`  🧹 Step 3.5: Code-based cleaning — junk:${ds.junkRemoved} commercial:${ds.commercialRemoved} dupes:${ds.duplicatesRemoved} kvDupes:${ds.kvDuplicatesRemoved} words:${ds.repeatedWordsRemoved} → ${ds.reductionPercent}% reduction`, 'info');
                                    }
                                }
                            } else {
                                const errMsg = primarySettled.status === 'rejected'
                                    ? primarySettled.reason?.message
                                    : primarySettled.value?.error || 'Unknown error';
                                addLog(`  ❌ Primary scrape failed: ${errMsg}`, 'error');
                            }

                            // Show primary result preview
                            if (primaryContent && !anyHold) {
                                const step3aDecision = await waitForApproval(
                                    'Primary Scraped Content (Step 3a)', 3,
                                    {
                                        type: 'scraped_content',
                                        url: urlsToScrape[0],
                                        label: 'Primary URL',
                                        contentLength: primaryContent.length,
                                        filteredCharCount: (primaryContent.match(/[A-Za-z0-9]/g) || []).length,
                                        preview: primaryContent,
                                        hasSecondary: urlsToScrape.length > 1
                                    },
                                    { rowNum, identifier }
                                );
                                if (step3aDecision === 'skip') {
                                    addLog(`  ⏭ Row skipped by user`, 'info');
                                    rowSkipped = true; skipReason = 'user';
                                } else if (typeof step3aDecision === 'string' && step3aDecision !== 'approve') {
                                    primaryContent = step3aDecision;
                                    addLog(`  ✏️ Primary content edited by user (${primaryContent.length} chars)`, 'detail');
                                }
                            }

                            // ── Process secondary result ──
                            if (!rowSkipped && scrapeResults.length > 1) {
                                const secondarySettled = scrapeResults[1];
                                if (secondarySettled.status === 'fulfilled' && secondarySettled.value.success) {
                                    const secondaryResult = secondarySettled.value;
                                    secondaryContent = secondaryResult.cleanedContent || '';
                                    addLog(`  ✅ Secondary scraped ${secondaryContent.length} chars from ${urlsToScrape[1]}`, 'success');
                                    if (secondaryResult.deepCleanStats) {
                                        const ds = secondaryResult.deepCleanStats;
                                        addLog(`  🧹 Step 3.5: Secondary cleaning — junk:${ds.junkRemoved} commercial:${ds.commercialRemoved} dupes:${ds.duplicatesRemoved} → ${ds.reductionPercent}% reduction`, 'info');
                                    }
                                } else {
                                    const errMsg = secondarySettled.status === 'rejected'
                                        ? secondarySettled.reason?.message
                                        : secondarySettled.value?.error || 'Unknown error';
                                    addLog(`  ⚠️ Secondary scrape failed: ${errMsg} — continuing with primary only`, 'warning');
                                }

                                // Show secondary result preview
                                if (secondaryContent) {
                                    const step3bDecision = await waitForApproval(
                                        'Secondary Scraped Content (Step 3b)', 3,
                                        {
                                            type: 'scraped_content',
                                            url: urlsToScrape[1],
                                            label: 'Secondary URL',
                                            contentLength: secondaryContent.length,
                                            filteredCharCount: (secondaryContent.match(/[A-Za-z0-9]/g) || []).length,
                                            preview: secondaryContent
                                        },
                                        { rowNum, identifier }
                                    );
                                    if (step3bDecision === 'skip') {
                                        addLog(`  ⏭ Secondary content skipped by user — using primary only`, 'info');
                                        secondaryContent = '';
                                    } else if (typeof step3bDecision === 'string' && step3bDecision !== 'approve') {
                                        secondaryContent = step3bDecision;
                                        addLog(`  ✏️ Secondary content edited by user (${secondaryContent.length} chars)`, 'detail');
                                    }
                                }
                            }

                            if (!primaryContent && !secondaryContent) {
                                addLog(`  ⚠️ All scraping failed — falling back to description-only mode`, 'warning');
                                logEntry('SCRAPE', 'All scraping failed — fallback to description-only', 'warning', { primaryUrl: bestUrl, secondaryUrl });
                                descriptionOnlyMode = true;
                            } else if (anyHold) {
                                // Combine what we have but mark as hold
                                scrapedContent = secondaryContent
                                    ? primaryContent + '\n\n=== SECONDARY SOURCE ===\n' + secondaryContent
                                    : primaryContent;
                                holdMode = true;
                            } else if (!rowSkipped) {
                                // Combine primary + secondary content
                                scrapedContent = secondaryContent
                                    ? primaryContent + '\n\n=== SECONDARY SOURCE ===\n' + secondaryContent
                                    : primaryContent;

                                addLog(`  ✅ Step 3 Complete: Total ${scrapedContent.length} chars${secondaryContent ? ' (combined from 2 sources)' : ''}`, 'success');
                                logEntry('SCRAPE', `Scraped ${scrapedContent.length} chars`, 'success', {
                                    primaryUrl: bestUrl,
                                    secondaryUrl: secondaryUrl || null,
                                    primaryLength: primaryContent.length,
                                    secondaryLength: secondaryContent.length,
                                    combinedLength: scrapedContent.length
                                });
                                await saveStage('scrape', { sourceUrl: bestUrl, secondaryUrl, scrapedContent });
                            }
                        }
                    }

                    await checkMidStep(); // Pause/Stop check before Step 4
                    // ═══ STEP 4: AI SPEC EXTRACTION (Gemini 2.5 Flash Lite) ═══
                    if (!rowSkipped && !descriptionOnlyMode && !holdMode && scrapedContent && getPipelineStepEnabled('step4')) {
                        stepTimer.start();
                        addLog(`  🧹 Step 4: AI Spec Extraction...`, 'step');
                        const cleanExtResp = await apiClient.stepCleanExtract({ scrapedContent, model: getSpecExtractModel() });
                        if (!cleanExtResp.success) {
                            addLog(`  ❌ Spec extraction failed: ${cleanExtResp.error}`, 'error');
                            addLog(`  ⚠️ Falling back to raw scraped content`, 'warning');
                        } else {
                            specContent = cleanExtResp.specContent;
                            const step4Decision = await waitForApproval(
                                'AI Spec Extraction', 4,
                                {
                                    type: 'spec_extraction',
                                    specContent: specContent,
                                    originalLength: cleanExtResp.originalLength,
                                    cleanedLength: cleanExtResp.cleanedLength,
                                    specLength: cleanExtResp.specLength,
                                    preview: specContent.substring(0, 6000)
                                },
                                { rowNum, identifier }
                            );
                            if (step4Decision === 'skip') { rowSkipped = true; skipReason = 'user'; }
                            else {
                                addLog(`  ✅ Specs extracted: ${cleanExtResp.originalLength} → ${cleanExtResp.specLength} chars`, 'success');
                                logEntry('SPEC_EXTRACT', `Extracted specs (${cleanExtResp.specLength} chars)`, 'success', {
                                    originalLength: cleanExtResp.originalLength,
                                    cleanedLength: cleanExtResp.cleanedLength,
                                    specLength: cleanExtResp.specLength,
                                    specContent
                                });
                                await saveStage('spec_extract', {});
                            }
                        }
                    }

                    await checkMidStep(); // Pause/Stop check before Step 4.5
                    // ═══ STEP 4.5: CLEAN NOUN/MODIFIER FROM EXTRACTED CONTENT ═══
                    if (!rowSkipped && !descriptionOnlyMode && !holdMode && specContent && getPipelineStepEnabled('step4_5')) {
                        const nm = (row.nounModifier || '').trim();
                        if (nm) {
                            addLog(`  🧼 Step 4.5: Removing Noun/Modifier "${nm}" from spec content...`, 'step');
                            const beforeLen = specContent.length;
                            // Split Noun/Modifier by comma to handle values like "BEARING, BALL"
                            const nmParts = nm.split(/[,\/]/).map(p => p.trim()).filter(Boolean);
                            let cleaned = specContent;
                            for (const part of nmParts) {
                                // Exact word boundary match, case-insensitive, global
                                const escaped = part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                const regex = new RegExp(`\\b${escaped}\\b`, 'gi');
                                cleaned = cleaned.replace(regex, '').replace(/\s{2,}/g, ' ');
                            }
                            // Also remove the full Noun/Modifier as-is (e.g. "BEARING, BALL" as one phrase)
                            if (nmParts.length > 1) {
                                const escapedFull = nm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                const fullRegex = new RegExp(escapedFull, 'gi');
                                cleaned = cleaned.replace(fullRegex, '').replace(/\s{2,}/g, ' ');
                            }
                            cleaned = cleaned.trim();
                            specContent = cleaned;
                            const removed = beforeLen - cleaned.length;
                            if (removed > 0) {
                                addLog(`  ✅ Removed ${removed} char(s) of Noun/Modifier text (${beforeLen} → ${cleaned.length})`, 'success');
                            } else {
                                addLog(`  ℹ️ No Noun/Modifier text found in spec content`, 'info');
                            }
                            logEntry('NM_CLEAN', `Cleaned Noun/Modifier from spec content`, 'success', {
                                nounModifier: nm, charsBefore: beforeLen, charsAfter: cleaned.length, removed
                            });
                        }
                    }

                    // ═══ STEP 5: SKIPPED (AI Model handles mapping directly) ═══
                    // Property prompt generation removed — using trained AI model instead

                    // ═══ STEP 6: AI EXTRACT (Engine-Routed) ═══════════════════════
                    if (!rowSkipped && !holdMode && getPipelineStepEnabled('step6')) {
                        await checkMidStep(); // Pause/Stop check before Step 6
                        stepTimer.log('Step 4: Spec Extraction');
                        stepTimer.start();
                        const engine = getExtractionEngine();
                        const engineLabel = engine === 'flan-t5' ? '🤖 Flan-T5' : engine === 'openai-luna' ? '🌙 GPT-5.6 Luna+Memory' : '✨ Gemini+Memory';
                        addLog(`  ${engine === 'flan-t5' ? '🤖' : engine === 'openai-luna' ? '🌙' : '✨'} Step 6: Extracting attributes via ${engineLabel}...`, 'step');

                        let extractResp;
                        if (engine === 'flan-t5') {
                            // ── Flan-T5 Custom Model (with Gemini fallback) ──
                            try {
                                extractResp = await apiClient.stepModelExtract({
                                    cleanedContent: specContent || scrapedContent || description,
                                    attributeLines,
                                    description,
                                    nounModifier: (row.nounModifier || '').trim(),
                                });
                                if (!extractResp.success) {
                                    addLog(`  ⚠️ Flan-T5 unavailable: ${extractResp.error || 'unknown'} — falling back to Gemini`, 'warning');
                                    extractResp = await apiClient.stepExtract({
                                        cleanedContent: specContent || scrapedContent || description,
                                        attributeLines,
                                        description,
                                        nounModifier: (row.nounModifier || '').trim(),
                                    });
                                }
                            } catch (modelErr) {
                                addLog(`  ⚠️ Flan-T5 failed: ${modelErr.message} — falling back to Gemini`, 'warning');
                                extractResp = await apiClient.stepExtract({
                                    cleanedContent: specContent || scrapedContent || description,
                                    attributeLines,
                                    description,
                                    nounModifier: (row.nounModifier || '').trim(),
                                });
                            }
                        } else {
                            // ── Gemini + Dynamic Few-Shot Memory ──
                            try {
                                extractResp = await apiClient.stepExtractWithMemory({
                                    cleanedContent: specContent || scrapedContent || description,
                                    attributeLines,
                                    description,
                                    nounModifier: (row.nounModifier || '').trim(),
                                    manufacturerName: step2MfrName || '',
                                    provider: engine === 'openai-luna' ? 'openai' : 'gemini',
                                    model: engine === 'openai-luna' ? 'gpt-5.6-luna' : getGeminiExtractionModel(),
                                    conditionModel: getConditionGenerationModel(),
                                });
                                if (extractResp.success) {
                                    const tierEmoji = { tier1_both: '🎯', tier2_partial: '⚡', tier2_nm_only: '📂', tier2_mfr_only: '🏭', tier3_default: '🔲' };
                                    addLog(`  📚 Few-shot: ${extractResp.fewShotExamplesUsed || 0} examples (${tierEmoji[extractResp.matchTier] || '❓'} ${extractResp.matchTier || 'unknown'})`, 'info');
                                    // Step 6 — Missing Property Conditions: rules from the DB and rules the AI wrote
                                    if (extractResp.conditionMemory) {
                                        const conditionLog = describeConditionMemory(extractResp.conditionMemory, { conditionModel: getConditionGenerationModel() });
                                        conditionLog.lines.forEach(([message, level]) => addLog(message, level));
                                        logEntry('CONDITION_MEMORY', conditionLog.action, conditionLog.result, conditionLog.data);
                                    }
                                }
                            } catch (memErr) {
                                addLog(`  ⚠️ Gemini+Memory failed: ${memErr.message} — trying plain Gemini`, 'warning');
                                logEntry('EXTRACT', `Gemini+Memory error: ${memErr.message} — falling back`, 'warning', { error: memErr.message });
                                extractResp = await apiClient.stepExtract({
                                    cleanedContent: specContent || scrapedContent || description,
                                    attributeLines,
                                    description,
                                    nounModifier: (row.nounModifier || '').trim(),
                                });
                            }
                        }

                        const provider = extractResp.provider || 'gemini';
                        const providerIcon = provider === 'local-model' ? '🤖' : provider === 'gemini-fewshot' ? '✨' : '☁️';

                        if (!extractResp.success) {
                            addLog(`  ❌ Extraction failed (${provider}): ${extractResp.error}`, 'error');
                            logEntry('EXTRACT', `Extraction failed (${provider}): ${extractResp.error}`, 'error', { provider, error: extractResp.error });
                            rowSkipped = true; skipReason = 'auto';
                        }
                        else {
                            extractedAttrs = extractResp.attributes;
                            mappedAttrs = extractResp.mapped;
                            addLog(`  ${providerIcon} Extracted via ${provider} (model: ${extractResp.extractionModel || 'default'})`, 'success');
                            logEntry('EXTRACT', `Extracted via ${provider} (model: ${extractResp.extractionModel || 'default'})`, 'success', {
                                attributeCount: extractResp.attributeCount,
                                provider,
                                model: extractResp.extractionModel,
                                fewShotExamplesUsed: extractResp.fewShotExamplesUsed || 0,
                            });


                            // ── Step 6.5 (Custom Code): remove extracted values that exactly match the Part Number or Noun/Modifier ──
                            if (getPipelineStepEnabled('step6_5') && extractedAttrs) {
                                const cleanupInput = {
                                    partNumbers: [row.partNumber, mfrPN, splPN, row.supplierPartNumber2, row.supplierPartNumber3, row.supplierPartNumber4],
                                    nounModifier: row.nounModifier || '',
                                };
                                const cleanup = removePartNumberAndNounModifier(extractedAttrs, cleanupInput);
                                extractedAttrs = cleanup.data;
                                if (cleanup.removed.length > 0) {
                                    addLog(`  🧼 Step 6.5 Custom Code: removed ${cleanup.removed.length} value(s) matching the Part Number / Noun/Modifier`, 'info');
                                    cleanup.removed.forEach(r => addLog(`     − ${r.field}: "${r.value}" (${r.matched})`, 'detail'));
                                } else {
                                    addLog(`  🧼 Step 6.5 Custom Code: no Part Number / Noun/Modifier values found in extracted data`, 'detail');
                                }
                                logEntry('CUSTOM_CLEAN', cleanup.removed.length > 0
                                    ? `Removed ${cleanup.removed.length} value(s) matching the Part Number / Noun/Modifier`
                                    : 'No Part Number / Noun/Modifier values in extracted data', 'success', {
                                    partNumbers: cleanup.terms.partNumbers,
                                    nounModifier: cleanup.terms.nounModifier,
                                    removed: cleanup.removed,
                                    extractedData: extractedAttrs,
                                });
                            } else if (!getPipelineStepEnabled('step6_5')) {
                                addLog(`  ⏭️ Step 6.5: Custom Code cleanup — SKIPPED (disabled in settings)`, 'warning');
                            }

                            const step6Decision = await waitForApproval(
                                'Extracted Attributes', 6,
                                { type: 'extracted_attributes', attributes: extractedAttrs, mapped: mappedAttrs, attributeCount: extractResp.attributeCount },
                                { rowNum, identifier }
                            );
                            if (step6Decision === 'skip') {
                                rowSkipped = true; skipReason = 'user';
                                addLog(`  ⏭ Extraction skipped by user`, 'info');
                                logEntry('EXTRACT', 'Skipped by user at approval', 'skipped');
                            }
                            else {
                                addLog(`  ✅ Extracted ${extractResp.attributeCount} attribute(s)`, 'success');
                                logEntry('EXTRACT', `Extracted ${extractResp.attributeCount} attribute(s)`, 'success', {
                                    attributeCount: extractResp.attributeCount,
                                    extractedAttrs,
                                    mappedAttrs,
                                    fullPrompt: extractResp.fullPrompt || ''
                                });
                            }
                        }
                        stepTimer.log('Step 6: AI Extraction');
                    }

                    // ── Final state update to IndexedDB ──
                    const isSuccess = !rowSkipped && !holdMode && extractedAttrs;
                    let rowState = 'failed';
                    if (isSuccess) rowState = 'completed';
                    else if (holdMode) rowState = 'hold';
                    else if (rowSkipped && skipReason === 'user') rowState = 'skipped';
                    else if (rowSkipped && skipReason === 'no_match') rowState = 'no_match'; // no exact part number match
                    else if (rowSkipped) rowState = 'failed'; // auto-skip due to pipeline failure

                    const totalRecordTime = ((performance.now() - recordStartTime) / 1000).toFixed(1);
                    addLog(`  ⏱ Total record time: ${totalRecordTime}s`, rowState === 'completed' ? 'success' : 'warning');
                    if (rowState === 'completed') logEntry('COMPLETE', `Pipeline completed successfully in ${totalRecordTime}s`, 'success');
                    else if (rowState === 'hold') logEntry('COMPLETE', `Content held for manual review (${scrapedContent?.length || 0} chars)`, 'hold');
                    else if (rowState === 'skipped') logEntry('COMPLETE', 'Row skipped', 'error');
                    else if (rowState === 'no_match') logEntry('COMPLETE', 'No Match Found — exited: no link shows the exact part number', 'error');
                    else logEntry('COMPLETE', 'Pipeline failed', 'error');

                    // Final incremental save with completed state
                    // Inject manufacturer/supplier metadata from input row into AI output
                    const finalAttrs = isSuccess ? injectManufacturerMeta(extractedAttrs, {
                        ...row,
                        classifiedManufacturers: classifiedMfrs,
                        classifiedVendors: classifiedVendors,
                        sourceUrl: bestUrl,
                        secondaryUrl: secondaryUrl,
                    }) : null;

                    // ── Auto-capture training data: ScrapedContent (X) → PropertyValues (Y) ──
                    if (isSuccess && finalAttrs && scrapedContent) {
                        try {
                            await apiClient.saveTrainingPair({
                                scrapedContent: scrapedContent,
                                description: description || '',
                                description1,
                                description2,
                                nounModifier: (row.nounModifier || '').trim(),
                                propertyNames: attributeLines || [],
                                mappedOutput: finalAttrs,
                                manufacturerPn: row.manufacturerPartNumber || row['MANUFACTURER PART NUMBER 1'] || '',
                                manufacturerName: step2MfrName || '',
                                sourceUrl: bestUrl || null,
                                provider: extractResp?.provider || 'gemini',
                            });
                            if (extractResp?.conditionMemory?.drafts?.length) {
                                try {
                                    await apiClient.savePropertyConditions({
                                        conditions: extractResp.conditionMemory.drafts,
                                        nounModifier: (row.nounModifier || '').trim(),
                                        manufacturerName: step2MfrName || '',
                                    });
                                    addLog(`  Conditions saved automatically with completed data (${extractResp.conditionMemory.drafts.length})`, 'detail');
                                    logEntry('CONDITION_MEMORY', `Saved ${extractResp.conditionMemory.drafts.length} AI-written rule(s) to Property Conditions`, 'success', {
                                        saved: extractResp.conditionMemory.drafts.map(d => ({ property: d.propertyName, condition: d.conditionText })),
                                    });
                                } catch (conditionSaveError) {
                                    addLog(`  Data saved, but condition memory save failed: ${conditionSaveError.message}`, 'warning');
                                    logEntry('CONDITION_MEMORY', 'Product data saved, but saving the AI-written rules failed', 'warning', { error: conditionSaveError.message });
                                }
                            }
                            addLog(`  📊 Training data captured`, 'detail');
                        } catch (trainErr) {
                            console.warn('[TrainingData] Capture failed (non-blocking):', trainErr.message);
                        }
                    }

                    await saveStage('complete', {
                        chatgptOutput: finalAttrs ? JSON.stringify(finalAttrs) : null,
                        chatgptSuccess: isSuccess,
                        chatgptRaw: isSuccess ? JSON.stringify(mappedAttrs) : null,
                        chatgptError: rowState === 'no_match' ? 'No Match Found' : rowState === 'skipped' ? 'Skipped by user' : (rowState === 'hold' ? 'Content size limit exceeded' : (rowState === 'failed' ? (skipReason === 'auto' ? 'Pipeline step failed' : 'Pipeline failed') : (isSuccess ? null : 'Pipeline failed'))),
                        sourceType: descriptionOnlyMode ? 'Description' : 'Online',
                        sourceUrl: bestUrl || null,
                        dataSource,
                        reviewStatus: rowState === 'completed' ? 'PENDING' : rowState === 'no_match' ? 'NO_MATCH' : (rowState === 'hold' ? 'HOLD' : 'REVIEW'),
                        generatedPrompt: null,
                        promptProperties: attributeLines || [],
                        classifiedManufacturers: classifiedMfrs,
                        classifiedVendors: classifiedVendors,
                        nounModifier: (row.nounModifier || '').trim(),
                        searchTier,
                        status: rowState,
                        scrapedContent: holdMode ? scrapedContent : null,
                    });

                    if (rowState === 'completed') {
                        addLog(`  ✅ Row complete!`, 'success');
                        setEnrichmentStats(prev => ({ ...prev, completed: prev.completed + 1 }));
                    } else if (rowState === 'hold') {
                        addLog(`  ⏸ Row held for review.`, 'warning');
                        setEnrichmentStats(prev => ({ ...prev, completed: prev.completed + 1 }));
                    } else if (rowState === 'skipped' || rowState === 'failed' || rowState === 'no_match') {
                        setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                    }

                } catch (err) {
                    // Handle pipeline control signals
                    if (err.message === PIPELINE_STOP) {
                        addLog('  🛑 Record interrupted — pipeline stopping.', 'warning');
                        if (dbId) {
                            try { await updateItem(dbId, { status: 'stopped', reviewStatus: 'REVIEW', pipelineStep: 'stopped' }); } catch (_) { }
                        }
                        break;
                    }
                    if (err.message === PIPELINE_SKIP) {
                        addLog('  ⏭ Record skipped by user.', 'info');
                        if (dbId) {
                            try { await updateItem(dbId, { status: 'skipped', reviewStatus: 'REVIEW', pipelineStep: 'skipped', chatgptError: 'Skipped by user' }); } catch (_) { }
                        }
                        setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                    } else {
                        addLog(`  ❌ Error: ${err.message}`, 'error');
                        setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                        await saveStage('error', {
                            chatgptOutput: null, chatgptSuccess: false, chatgptError: err.message,
                            reviewStatus: 'REVIEW', status: 'failed',
                        });
                    }
                }

                addLog('─'.repeat(60), 'divider');
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            addLog('\n💾 All records saved incrementally to IndexedDB.', 'success');

            if (stopRef.current) {
                addLog('\n🛑 Rescrape Stopped!', 'header');
                setEnrichmentStats(prev => {
                    addLog(`   Total: ${prev.total} | ✅ Success: ${prev.completed} | ⏭ Skipped/Failed: ${prev.failed} | ⏳ Remaining: ${prev.total - prev.completed - prev.failed}`, 'info');
                    return prev;
                });
                setPipelineState('stopped');
                toast.warning('Rescrape stopped. Completed records are preserved.');
            } else {
                addLog('\n🏁 Rescrape Complete!', 'header');
                setEnrichmentStats(prev => {
                    addLog(`   Total: ${prev.total} | ✅ Success: ${prev.completed} | ⏭ Skipped/Failed: ${prev.failed}`, 'info');
                    return prev;
                });
                setPipelineState('completed');
                toast.success('Rescrape completed! Redirecting to Results...');
                setRescrapeData(null);
                setTimeout(() => navigate('/results', { state: { reload: Date.now() } }), 2000);
            }
        } catch (error) {
            console.error('Rescrape pipeline error:', error);
            addLog(`\n💥 Rescrape Error: ${error?.message || String(error) || 'Unknown error'}`, 'error');
            toast.error(`Rescrape failed: ${error?.message || String(error) || 'Unknown error'}`);
            setPipelineState('stopped');
        } finally {
            setApiTraceRecorder(null);
            setEnrichmentRunning(false);
            setStepReview(null);
        }
    };

    // ── Main Pipeline with Step-by-Step Approval ─────────────────────
    const startEnrichmentProcess = async () => {
        if (!files.input) {
            toast.error('Please upload the Input Data Excel file');
            return;
        }
        if (!files.inc) {
            toast.error('INC File is mandatory — it provides the schema properties for dynamic prompt generation.');
            return;
        }

        setEnrichmentRunning(true);
        setEnrichmentLogs([]);
        setEnrichmentStats({ total: 0, completed: 0, failed: 0, current: 0 });
        // Reset pipeline control refs
        pauseRef.current = false;
        stopRef.current = false;
        skipCurrentRef.current = false;
        setPipelineState('running');
        pipelineStartTimeRef.current = Date.now();
        setCurrentStepName('');
        const enrichmentResults = [];

        if (processingModeRef.current === 'cleansed') {
            addLog('⚡ Cleansed Data mode: Input + INC → Step 0 (DB check) → Step 6 (AI extraction).', 'info');
            addLog('   Skipping search, classification, scraping and spec extraction. The row description is the extraction source.', 'info');
        } else {
            addLog('✨ Enrichment Data mode: running the full pipeline.', 'info');
        }

        try {
            addLog('🚀 Starting Enrichment Pipeline (Manual Verification)...', 'header');
            addLog('Parsing Input Data Excel...', 'info');

            const inputData = await parseExcelFile(files.input);
            const initialEnrichableCount = inputData.filter(row =>
                row.manufacturerNumber?.trim() || row.givenSupplierPartnumber?.trim() || combineDescriptions(row)
            ).length;
            setEnrichmentStats(prev => ({ ...prev, total: initialEnrichableCount }));
            setCurrentStepName('Preparing INC schema');
            await new Promise(resolve => setTimeout(resolve, 0));
            addLog(`✅ Parsed ${inputData.length} row(s) from Excel`, 'success');
            for (const warning of validateExcelData(inputData).warnings || []) {
                addLog(`  ⚠️ ${warning}`, 'warning');
            }

            let incDataParsed = [];
            if (files.inc) {
                addLog('Parsing INC Data Excel...', 'info');
                incDataParsed = await parseIncExcel(files.inc);
                addLog(`✅ Parsed ${incDataParsed.length} INC row(s)`, 'success');
                // Persist INC data to IndexedDB for future rescrape use
                try {
                    const incValidation = validateIncData(incDataParsed);
                    if (!incValidation.valid) throw new Error(incValidation.errors.join('; '));
                    if (incDataParsed.length <= 10000) {
                        await clearIncData();
                        await addIncData(incDataParsed);
                    } else {
                        addLog(`Large INC file (${incDataParsed.length.toLocaleString()} rows) loaded in memory; browser caching skipped`, 'info');
                    }
                    addLog('  💾 INC data saved to local DB for rescrape reuse', 'info');
                } catch (incSaveErr) {
                    console.warn('Failed to save INC data to IndexedDB:', incSaveErr);
                }
            }

            const buPartAValues = await fetchActiveBUPartAValues();

            const enrichableRows = inputData.filter(row =>
                (row.manufacturerNumber && row.manufacturerNumber.trim()) ||
                (row.givenSupplierPartnumber && row.givenSupplierPartnumber.trim()) ||
                combineDescriptions(row)
            );

            if (enrichableRows.length === 0) {
                addLog('❌ No enrichable rows found (need Part # or Description)', 'error');
                toast.error('No enrichable rows found');
                setEnrichmentRunning(false);
                return;
            }

            addLog(`📋 ${enrichableRows.length} enrichable row(s) found`, 'info');

            // ── Noun/Modifier DB Resolution (strict — no Excel fallback) ──
            addLog('🔍 Resolving Noun/Modifier from DB...', 'step');
            try {
                // Collect all possible part numbers to look up
                const allPartNumbers = [];
                for (const row of enrichableRows) {
                    if (row.manufacturerNumber?.trim()) allPartNumbers.push(row.manufacturerNumber.trim());
                    if (row.givenSupplierPartnumber?.trim()) allPartNumbers.push(row.givenSupplierPartnumber.trim());
                    if (row.supplierPartNumber2?.trim()) allPartNumbers.push(row.supplierPartNumber2.trim());
                    if (row.supplierPartNumber3?.trim()) allPartNumbers.push(row.supplierPartNumber3.trim());
                    if (row.supplierPartNumber4?.trim()) allPartNumbers.push(row.supplierPartNumber4.trim());
                    if (row.partnum?.trim()) allPartNumbers.push(row.partnum.trim());
                }
                const nmMap = allPartNumbers.length > 0
                    ? (await apiClient.nounModifierBatchLookup(allPartNumbers)).map || {}
                    : {};
                let dbMatched = 0, pending = 0;
                for (const row of enrichableRows) {
                    const keysToTry = [
                        row.manufacturerNumber?.trim(),
                        row.givenSupplierPartnumber?.trim(),
                        row.supplierPartNumber2?.trim(),
                        row.supplierPartNumber3?.trim(),
                        row.supplierPartNumber4?.trim(),
                        row.partnum?.trim()
                    ].filter(Boolean);
                    let found = false;
                    for (const k of keysToTry) {
                        const uk = k.toUpperCase();
                        if (nmMap[uk]) {
                            row.nounModifier = nmMap[uk].nounModifier;
                            row._nmPending = false;
                            found = true;
                            dbMatched++;
                            break;
                        }
                    }
                    if (!found) {
                        // Fallback: use Noun/Modifier from Excel if available
                        if (row.nounModifier && row.nounModifier.trim()) {
                            addLog(`  ℹ️ Row ${row.rowNumber || '?'}: NM not in DB — using Excel value "${row.nounModifier.trim()}"`, 'detail');
                            row._nmPending = false;
                            dbMatched++; // count as resolved (from Excel)
                        } else {
                            row.nounModifier = '';
                            row._nmPending = true;
                            pending++;
                        }
                    }
                }
                addLog(`  ✅ Noun/Modifier: ${dbMatched} resolved from DB`, 'success');
                if (pending > 0) addLog(`  ⚠ ${pending} row(s) missing — will be marked as "Noun/Modifier Pending"`, 'warning');
            } catch (nmErr) {
                addLog(`  ❌ Noun/Modifier DB lookup failed: ${nmErr.message}`, 'error');
                // Mark all as pending if lookup fails
                for (const row of enrichableRows) { row.nounModifier = ''; row._nmPending = true; }
            }

            // Build INC lookup for dynamic property resolution per-row
            const incLookup = buildIncLookup(incDataParsed);
            addLog(`📋 INC schema loaded: ${incLookup.size} Noun/Modifier group(s) mapped`, 'step');

            addLog('─'.repeat(60), 'divider');
            setEnrichmentStats(prev => ({ ...prev, total: enrichableRows.length }));

            for (let i = 0; i < enrichableRows.length; i++) {
                const row = enrichableRows[i];
                setApiTraceRecorder(null);
                const rowNum = row.rowNumber || (i + 1);
                const splName = (row.givenSupplierName || '').trim();
                const mfrName = (row.manufacturerName || '').trim();
                // Pre-condition: if supplierName is null, use manufacturerName
                const companyName = splName || mfrName || '';
                const mfrPN = (row.manufacturerNumber || '').trim();
                const splPN = (row.givenSupplierPartnumber || '').trim();
                const identifier = (row.partNumber || '').trim() || mfrPN || splPN || '(description-only)';
                const { description1, description2 } = getDescriptions(row);
                const description = combineDescriptions({ description1, description2 });

                setEnrichmentStats(prev => ({ ...prev, current: i + 1 }));

                // ── Pipeline Control Checks ──
                const controlResult = await checkPipelineControls(addLog);
                if (controlResult === 'break') break;
                if (controlResult === 'continue') {
                    setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                    continue;
                }

                addLog(`\n▶ Row ${i + 1}/${enrichableRows.length}: ${identifier}`, 'header');

                // ── Noun/Modifier Pending Gate ──────────────────────
                // Only block rows that HAVE part numbers but no NM resolved.
                // Description-only rows (no PN) can proceed — they'll use description-only mode.
                const hasAnyPN = !!(mfrPN || splPN);
                if (row._nmPending && hasAnyPN) {
                    addLog(`  ⏸ SKIPPED — Noun/Modifier not found in DB. Add it via Noun/Modifier Master page.`, 'warning');
                    try {
                        await addItem({
                            ...row,
                            rowNumber: rowNum,
                            description,
                            givenSupplierName: splName || '',
                            givenSupplierPartnumber: splPN,
                            manufacturerName: companyName,
                            manufacturerNumber: mfrPN,
                            nounModifier: '',
                            chatgptOutput: null,
                            chatgptSuccess: false,
                            chatgptModel: 'enrichment-pipeline',
                            sourceType: 'Online',
                            reviewStatus: 'NM_PENDING',
                            status: 'nm_pending',
                            pipelineStep: 'noun-modifier-pending',
                            processingLog: [{ stage: 'NM_CHECK', action: 'Noun/Modifier not in DB', result: 'pending', timestamp: new Date().toISOString() }],
                        });
                    } catch (_) { }
                    setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                    continue;
                }
                // ── Dynamic Schema Resolution from INC ──────────────────
                const attributeLines = getOrderedProperties(row.nounModifier, incLookup);
                addLog(`  📋 Schema: ${attributeLines.length > 0 ? attributeLines.length + ' properties (' + attributeLines.slice(0, 5).join(', ') + (attributeLines.length > 5 ? '...' : '') + ')' : 'NONE'}`, attributeLines.length > 0 ? 'step' : 'warning');

                if (attributeLines.length === 0) {
                    addLog(`  ❌ SKIPPED — No schema properties found in INC for NM "${row.nounModifier || '(empty)'}". Add this NM to the INC file.`, 'error');
                    try {
                        await addItem({
                            ...row, rowNumber: rowNum, description1, description2, description,
                            givenSupplierName: splName || '', givenSupplierPartnumber: splPN,
                            manufacturerName: companyName, manufacturerNumber: mfrPN,
                            nounModifier: row.nounModifier || '',
                            chatgptOutput: null, chatgptSuccess: false, chatgptModel: 'enrichment-pipeline',
                            sourceType: 'Online', reviewStatus: 'SCHEMA_MISSING', status: 'schema_missing',
                            pipelineStep: 'schema-missing',
                            processingLog: [{ stage: 'SCHEMA', action: `No INC properties for NM "${row.nounModifier}"`, result: 'error', timestamp: new Date().toISOString() }],
                        });
                    } catch (_) { }
                    setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                    continue;
                }


                // ── Property prompt pre-check removed — AI Model handles mapping directly ──

                let searchResults = null;
                let bestUrl = null;
                let secondaryUrl = null; // vendor link — also scraped only when manufacturer confidence is 90% or lower
                let fallbackUrl = null; // link not chosen for scraping — used if the 1st link is only a homepage
                let scrapePlanInfo = null; // Step 2 scrape plan, for Step 3 logs
                let urlScore = null;
                let scrapedContent = null;
                let extractedAttrs = null;
                let mappedAttrs = null;
                let classifiedMfrs = [];
                let classifiedVendors = [];
                let rowSkipped = false;
                let skipReason = null; // 'user' or 'auto'
                let searchTier = 0;
                let descriptionOnlyMode = false;
                let dataSource = 'Vendor URL';
                let step2MfrName = '';  // manufacturer name from Step 2 classification
                let step2MfrPN = '';    // manufacturer part number from Step 2

                // Structured per-row processing log
                const rowLog = [];
                const logEntry = (stage, action, result, data = {}) => {
                    rowLog.push({ stage, action, result, data, timestamp: new Date().toISOString() });
                };
                // Every API call from here on is recorded in this row's processing log
                setApiTraceRecorder(entry => rowLog.push(entry));
                logEntry('INIT', 'Pipeline started', 'info', {
                    identifier, companyName, description,
                    supplierPN: splPN, manufacturerPN: mfrPN
                });

                // ── Incremental IndexedDB save: create record immediately ──
                let dbId = null;
                try {
                    dbId = await addItem({
                        ...row,
                        rowNumber: rowNum,
                        description,
                        givenSupplierName: splName || '',
                        givenSupplierPartnumber: splPN,
                        manufacturerName: companyName,
                        manufacturerNumber: mfrPN,
                        nounModifier: row.nounModifier || '',
                        chatgptOutput: null,
                        chatgptSuccess: false,
                        chatgptModel: 'enrichment-pipeline',
                        sourceType: 'Online',
                        reviewStatus: 'PROCESSING',
                        status: 'processing',
                        pipelineStep: 'init',
                        processingLog: rowLog,
                    });
                } catch (dbErr) {
                    console.error('IndexedDB init save failed:', dbErr);
                }

                // Helper: save stage data to IndexedDB (non-blocking)
                const saveStage = async (stage, data) => {
                    if (!dbId) return;
                    try {
                        await updateItem(dbId, { ...data, pipelineStep: stage, processingLog: rowLog });
                    } catch (e) {
                        console.error(`IndexedDB stage save failed (${stage}):`, e);
                    }
                };

                try {
                    // ═══ STEP 0: DB CHECK ═══════════════════════════════════
                    const checkPN = (mfrPN || splPN || '').trim();
                    if (checkPN && isStepEnabled('step0')) {
                        setCurrentStepName('Step 0: DB Check');
                        addLog(`  🗃️ Step 0: Checking Master DB for PN "${checkPN}"...`, 'step');
                        try {
                            const dbCheck = await apiClient.checkPartNumber(checkPN);
                            if (dbCheck.success && dbCheck.found) {
                                addLog(`  ✅ Found in Master DB! (matched: ${dbCheck.matchedColumn})`, 'success');
                                logEntry('DB_CHECK', `PN found in ${dbCheck.matchedColumn}`, 'match', { matchedColumn: dbCheck.matchedColumn });

                                const dbDecision = await waitForApproval(
                                    'Existing Data Found in Master DB',
                                    0,
                                    {
                                        type: 'db_match',
                                        partNumber: checkPN,
                                        matchedColumn: dbCheck.matchedColumn,
                                        data: dbCheck.data
                                    },
                                    { rowNum, identifier }
                                );

                                if (dbDecision === 'approve') {
                                    addLog(`  ✅ Using existing data from Master DB — skipping pipeline`, 'success');
                                    logEntry('DB_CHECK', 'Reusing existing data', 'success');
                                    logEntry('COMPLETE', 'Pipeline completed (DB match)', 'success');
                                    // Merge DB data with row input metadata so no fields are lost
                                    const mergedDbData = injectManufacturerMeta(dbCheck.data || {}, row);
                                    // Save as fully completed with all fields the results page needs
                                    await saveStage('complete', {
                                        sourceType: 'DB',
                                        chatgptOutput: JSON.stringify(mergedDbData),
                                        chatgptSuccess: true,
                                        chatgptRaw: JSON.stringify(dbCheck.data),
                                        chatgptError: null,
                                        reviewStatus: 'PENDING',
                                        status: 'completed',
                                        sourceUrl: null,
                                        searchTier: 0,
                                        processingLog: rowLog,
                                    });
                                    setEnrichmentStats(prev => ({ ...prev, completed: prev.completed + 1 }));
                                    addLog(`  ✅ Row complete! (DB match)`, 'success');
                                    addLog('─'.repeat(60), 'divider');
                                    continue;
                                } else {
                                    addLog(`  ▶️ User chose to re-scrape — proceeding with pipeline`, 'info');
                                    logEntry('DB_CHECK', 'User opted to re-scrape', 'info');
                                }
                            } else {
                                addLog(`  ℹ️ Not found in Master DB — proceeding with search`, 'info');
                                logEntry('DB_CHECK', 'Not found in Master DB', 'info', { partNumber: checkPN });
                            }
                        } catch (dbErr) {
                            addLog(`  ⚠️ DB check failed: ${dbErr.message} — proceeding with search`, 'warning');
                            logEntry('DB_CHECK', `DB check failed: ${dbErr.message}`, 'error');
                        }
                    }

                    await checkMidStep(); // Pause/Stop check between Step 0 → Step 1
                    // ═══ STEP 1: SUPPLIER-VALIDATED SEARCH ═══════════════════
                    if (!isStepEnabled('step1')) {
                        addLog(`  ⏭️ Step 1: Supplier Search — SKIPPED (disabled in settings)`, 'warning');
                    } else {
                    // Build supplier-PN pairs for domain validation
                    const supplierPNPairs = [
                        { supplier: (row.givenSupplierName || '').trim(), partNumber: splPN },
                        { supplier: (row.givenSupplierName2 || '').trim(), partNumber: (row.supplierPartNumber2 || '').trim() },
                    ].filter(p => p.supplier && p.partNumber);

                    const allSearchPNs = [...new Set(supplierPNPairs.map(p => p.partNumber).concat(mfrPN ? [mfrPN] : []))];

                    // Use nounModifier as fallback description for search when description is empty
                    // Pipeline setting "Description in Serper search": when OFF, Serper gets the Part Number only
                    const descriptionSearchOn = getSearchWithDescription();
                    const searchDesc = descriptionSearchOn ? (description1 || (row.nounModifier || '').trim()) : '';
                    const hasDesc = !!searchDesc;
                    setCurrentStepName('Step 1: Web Search');
                    addLog(`  🔍 Step 1: Supplier-Validated Search (${allSearchPNs.length} PNs${hasDesc ? ' + Description' : descriptionSearchOn ? '' : ' only — Description search OFF'})...`, 'step');
                    if (supplierPNPairs.length > 0) addLog(`    ↳ Pairs: ${supplierPNPairs.map(p => `${p.supplier}→"${p.partNumber}"`).join(', ')}`, 'detail');
                    if (allSearchPNs.length > 1) addLog(`    ↳ OR query: ${allSearchPNs.map(p => `"${p}"`).join(' OR ')}`, 'detail');
                    if (!description && searchDesc) addLog(`    ↳ No description — using Noun/Modifier "${searchDesc}" as fallback`, 'detail');
                    if (!descriptionSearchOn) addLog(`    ↳ Description search is OFF (Pipeline settings) — Serper gets the Part Number only; AI still picks the Manufacturer and Vendor websites`, 'detail');
                    else if (!hasDesc) addLog(`    ⚠️ No description or Noun/Modifier available for description search`, 'warning');
                    logEntry('SEARCH', descriptionSearchOn ? 'Starting supplier-validated search' : 'Starting supplier-validated search — Part Number only (Description search OFF)', 'info', { supplierPNPairs, manufacturerPN: mfrPN, descriptionSearch: descriptionSearchOn ? 'ON' : 'OFF', description: searchDesc?.substring(0, 100) || null, descSource: !descriptionSearchOn ? 'off' : description ? 'input' : searchDesc ? 'nounModifier' : 'none' });

                    const searchInput = {
                        description: searchDesc,
                        supplierPNPairs,
                        manufacturerPartNumber: mfrPN || undefined,
                    };

                    // Show what will be searched → wait for approval
                    const step1Decision = await waitForApproval(
                        'Supplier-Validated Search',
                        1,
                        {
                            type: 'search_validated_input',
                            supplierPNPairs,
                            manufacturerPartNumber: mfrPN || undefined,
                            description,
                            descriptionForSearch: searchDesc,
                            descriptionSearchOn,
                            companyName
                        },
                        { rowNum, identifier }
                    );

                    if (step1Decision === 'skip') {
                        addLog(`  ⏭ Row skipped by user`, 'info');
                        rowSkipped = true; skipReason = 'user';
                    }

                    if (!rowSkipped) {
                        addLog(`  🔍 Calling Serper API (supplier-validated search)...`, 'step');

                        // Use validated search if we have supplier-PN pairs, else fallback to basic search
                        let searchResp = supplierPNPairs.length > 0
                            ? await apiClient.stepSearchValidated(searchInput)
                            : await apiClient.stepSearch({ description: searchDesc, partNumbers: allSearchPNs, supplierPartNumber: splPN, manufacturerPartNumber: mfrPN });

                        const agentSettings = getAgentSettings();
                        if (searchResp.success && (!searchResp.results || searchResp.results.length === 0)
                            && agentSettings.enabled && agentSettings.triggerOnNoResults) {
                            addLog('  🤖 Agentic AI: normal search found no URLs; planning alternate searches...', 'step');
                            logEntry('AGENT', 'Starting Agentic URL Recovery', 'info', { settings: agentSettings });
                            try {
                                const recovery = await apiClient.agentRecoverUrls({
                                    supplierNames: supplierPNPairs.map(pair => pair.supplier),
                                    partNumbers: allSearchPNs,
                                    manufacturerPartNumber: mfrPN,
                                    description: searchDesc,
                                    nounModifier: descriptionSearchOn ? (row.nounModifier || '') : '',
                                    settings: agentSettings,
                                });
                                if (recovery.success && recovery.results?.length) {
                                    searchResp = {
                                        ...searchResp,
                                        results: recovery.results,
                                        totalResults: recovery.results.length,
                                        searchSources: { agentic: recovery.results.length },
                                        agentRecovery: recovery,
                                    };
                                    addLog(`  ✅ Agentic AI recovered ${recovery.results.length} candidate URL(s) using ${recovery.attemptedQueries.length} alternate query(s).`, 'success');
                                    logEntry('AGENT', 'Agentic URL Recovery found candidates', 'success', recovery);
                                } else {
                                    addLog('  ⚠️ Agentic AI could not recover a URL. Using description-only extraction.', 'warning');
                                    logEntry('AGENT', 'Agentic URL Recovery exhausted', 'warning', recovery);
                                }
                            } catch (agentError) {
                                addLog(`  ⚠️ Agentic URL Recovery failed: ${agentError.message}. Using description-only extraction.`, 'warning');
                                logEntry('AGENT', 'Agentic URL Recovery failed', 'error', { error: agentError.message });
                            }
                        }

                        if (!searchResp.success) {
                            addLog(`  ❌ Search failed: ${searchResp.error || 'Unknown error'}`, 'error');
                            logEntry('SEARCH', 'Search failed', 'error', { error: searchResp.error });
                            rowSkipped = true; skipReason = 'auto';
                        } else if (!searchResp.results || searchResp.results.length === 0) {
                            addLog(`  ⚠️ No search results found. Switching to AI description extraction.`, 'warning');
                            descriptionOnlyMode = true;
                            logEntry('SEARCH', 'No results found', 'warning');
                            await saveStage('search', { searchTier: 0, sourceType: 'Description' });
                        } else {
                            searchResults = searchResp.results;
                            const srcInfo = searchResp.searchSources || {};
                            const vReport = searchResp.validationReport;

                            addLog(`  ✅ Got ${searchResults.length} result(s) (PN: ${srcInfo.partNumber || 0}, Desc: ${srcInfo.description || 0})`, 'success');

                            logEntry('SEARCH', `Found ${searchResults.length} result(s)`, 'success', {
                                searchSources: srcInfo,
                                urls: searchResults.map(r => ({ title: r.title, url: r.link, snippet: r.snippet, domain: r.domain, searchSource: r.searchSource }))
                            });

                            // Show search results for user approval
                            const step1bDecision = await waitForApproval(
                                'Search Results',
                                1,
                                {
                                    type: 'search_validated_results',
                                    results: searchResults,
                                    validationReport: vReport,
                                    searchSources: srcInfo,
                                    totalResults: searchResults.length
                                },
                                { rowNum, identifier }
                            );

                            if (step1bDecision === 'skip') {
                                addLog(`  ⏭ Row skipped by user`, 'info');
                                rowSkipped = true; skipReason = 'user';
                            } else {
                                await saveStage('search', { searchTier: 1, sourceType: 'Online', validationReport: vReport });

                                // ═══ STEP 1.5: DB MANUFACTURER PRIORITY ═══════════════
                                let dbManufacturerInfo = null;
                                if (!isStepEnabled('step1_5')) {
                                    addLog(`  ⏭️ Step 1.5: DB MFR Priority — SKIPPED (disabled in settings)`, 'warning');
                                } else try {
                                    addLog(`  🏭 Step 1.5: Checking search results against DB manufacturers...`, 'step');
                                    const dbMatchResp = await apiClient.stepMatchDbManufacturers({ searchResults });
                                    if (dbMatchResp.success && dbMatchResp.matchCount > 0) {
                                        searchResults = dbMatchResp.prioritizedResults;
                                        // Capture first DB manufacturer match for Step 2
                                        const firstMatch = dbMatchResp.dbMatches[0];
                                        dbManufacturerInfo = {
                                            name: firstMatch.manufacturer,
                                            url: firstMatch.manufacturerUrl || ''
                                        };
                                        addLog(`  ✅ DB Manufacturer Priority: ${dbMatchResp.matchCount}/${dbMatchResp.totalResults} URLs matched DB manufacturers — moved to top`, 'success');
                                        for (const m of dbMatchResp.dbMatches) {
                                            addLog(`    ↳ ${m.manufacturer} (${m.matchType}) → ${m.domain || m.url}`, 'detail');
                                        }
                                        if (dbManufacturerInfo.url) {
                                            addLog(`    🔗 DB Manufacturer URL: ${dbManufacturerInfo.url} — will be passed to Step 2`, 'success');
                                        }
                                        logEntry('SEARCH', `DB manufacturer priority: ${dbMatchResp.matchCount} URLs boosted`, 'success', {
                                            matchCount: dbMatchResp.matchCount,
                                            dbMatches: dbMatchResp.dbMatches,
                                            dbManufacturerInfo
                                        });
                                    } else {
                                        addLog(`  ℹ️ No DB manufacturer matches found — proceeding with original order`, 'info');
                                    }
                                } catch (dbMatchErr) {
                                    addLog(`  ⚠️ DB match check failed: ${dbMatchErr.message} — proceeding with original order`, 'warning');
                                }

                                // ═══ STEP 2: AI URL SELECTION ═══════════════════
                                if (!isStepEnabled('step2')) {
                                    // Step 2 OFF → scrape the top search result (already DB-manufacturer-prioritised by Step 1.5)
                                    const topResult = searchResults?.[0];
                                    if (topResult?.link) {
                                        bestUrl = topResult.link;
                                        secondaryUrl = null;
                                        fallbackUrl = null;
                                        scrapePlanInfo = null;
                                        dataSource = 'Vendor URL';
                                        step2MfrName = dbManufacturerInfo?.name || '';
                                        step2MfrPN = mfrPN || splPN || '';
                                        addLog(`  ⏭️ Step 2: AI Link Ranking — SKIPPED (disabled in settings) → scraping top search result: ${bestUrl}`, 'warning');
                                        logEntry('AI_SELECT', 'Skipped (disabled in settings) — using top search result', 'skipped', { url: bestUrl, title: topResult.title || '', manufacturer: step2MfrName || null });
                                    } else {
                                        addLog(`  ⏭️ Step 2: AI Link Ranking — SKIPPED (disabled in settings), no search result to scrape`, 'warning');
                                    }
                                } else {
                                // ─── Step 2a: Preview classification prompt ───
                                const rankPayload = {
                                    urls: searchResults,
                                    description,
                                    manufacturerPartNumber: mfrPN,
                                    supplierPartNumber: splPN,
                                    classificationModel: getClassificationModel(),
                                    supplierNames: [splName, (row.givenSupplierName2 || '').trim()].filter(Boolean),
                                    dbManufacturer: dbManufacturerInfo,
                                    partNumbers: allSearchPNs, // exact part number match is checked against all of these
                                };

                                addLog(`  📝 Step 2: AI Link Ranking — manufacturer & vendor link with confidence...`, 'step');
                                logEntry('AI_SELECT', 'Starting AI link ranking', 'info', { urlCount: searchResults.length });
                                // ─── Step 2a: Preview classification prompt (ONLY if Preview ON) ───
                                const isPreviewActive2 = localStorage.getItem(PREVIEW_KEY) !== 'false';
                                if (isPreviewActive2) {
                                    const previewResp = await apiClient.stepRankPreview(rankPayload);
                                    if (previewResp.success) {
                                        const previewDecision = await waitForApproval(
                                            'Link Ranking Prompt',
                                            2,
                                            {
                                                type: 'classification_prompt_preview',
                                                fullPrompt: previewResp.fullPrompt,
                                                promptLength: previewResp.promptLength,
                                                sourceCount: previewResp.sourceCount,
                                                model: previewResp.model,
                                                sources: previewResp.sources
                                            },
                                            { rowNum, identifier }
                                        );
                                        if (previewDecision === 'skip') { addLog(`  ⏭ Row skipped by user`, 'info'); rowSkipped = true; skipReason = 'user'; }
                                    }
                                }

                                // ─── Step 2b: Send to AI ───
                                if (!rowSkipped) {
                                    addLog(`  🤖 Sending links to AI for ranking...`, 'step');
                                    const rankResp = await apiClient.stepRank(rankPayload);

                                    if (rankResp.success && rankResp.matchStatus === 'NO_MATCH_FOUND') {
                                        // Only links with an exact part number match may continue — exit with "No Match Found"
                                        const ranked = readRankResult(rankResp);
                                        classifiedMfrs = ranked.classifiedMfrs;
                                        classifiedVendors = ranked.classifiedVendors;
                                        ranked.logLines.forEach(([message, level]) => addLog(message, level));
                                        logEntry('AI_SELECT', ranked.summary, 'error', ranked.logData);
                                        rowSkipped = true; skipReason = 'no_match';
                                    } else if (!rankResp.success || (!rankResp.scrapable_url && !rankResp.manufacturer_url)) {
                                        addLog(`  ❌ No suitable URL found after AI selection`, 'error');
                                        rowSkipped = true; skipReason = 'auto';
                                    } else {
                                        const selMethod = rankResp.selectionMethod || 'AI_SELECTED';
                                        // Show AI-selected URLs → wait for approval
                                        const step2Decision = await waitForApproval(
                                            'AI Link Ranking',
                                            2,
                                            {
                                                type: 'url_selection',
                                                scrapable_url: rankResp.scrapable_url,
                                                manufacturer_url: rankResp.manufacturer_url || '',
                                                vendor_urls: rankResp.vendor_urls || [],
                                                manufacturers: rankResp.manufacturers || [],
                                                vendors: rankResp.vendors || [],
                                                selectionMethod: selMethod,
                                                manufacturer: rankResp.manufacturer || null,
                                                vendor: rankResp.vendor || null,
                                                scrapePlan: rankResp.scrapePlan || null,
                                                notes: rankResp.notes || [],
                                                searchResults: searchResults.map(r => ({ title: r.title, link: r.link, snippet: r.snippet, position: r.position, domain: r.domain, searchSource: r.searchSource }))
                                            },
                                            { rowNum, identifier }
                                        );

                                        if (step2Decision === 'skip') {
                                            addLog(`  ⏭ Row skipped by user`, 'info');
                                            rowSkipped = true; skipReason = 'user';
                                        } else {
                                            // Step 2 result: manufacturer + vendor link with confidence, and the scrape plan
                                            // (manufacturer first; vendor too only if manufacturer confidence is 90% or lower)
                                            const ranked = readRankResult(rankResp);
                                            classifiedMfrs = ranked.classifiedMfrs;
                                            classifiedVendors = ranked.classifiedVendors;
                                            bestUrl = ranked.bestUrl;
                                            secondaryUrl = ranked.secondaryUrl;
                                            fallbackUrl = ranked.fallbackUrl;
                                            scrapePlanInfo = ranked.plan;
                                            dataSource = ranked.dataSource;
                                            ranked.logLines.forEach(([message, level]) => addLog(message, level));
                                            logEntry('AI_SELECT', ranked.summary, 'success', ranked.logData);
                                            await saveStage('url_selection', { sourceUrl: bestUrl, secondaryUrl, selectionMethod: selMethod, classifiedManufacturers: classifiedMfrs, classifiedVendors: classifiedVendors, searchTier: 1 });
                                            // Capture manufacturer info from Step 2 for Step 2.5
                                            step2MfrName = classifiedMfrs[0]?.name || '';
                                            step2MfrPN = classifiedMfrs[0]?.partNumber || mfrPN || splPN || '';

                                            // Fallback: if AI didn't classify a manufacturer, use DB manufacturer from Step 1.5
                                            if (!step2MfrName && dbManufacturerInfo?.name) {
                                                step2MfrName = dbManufacturerInfo.name;
                                                step2MfrPN = mfrPN || splPN || '';
                                                addLog(`  ℹ️ AI didn't classify a manufacturer — using DB manufacturer "${step2MfrName}" from Step 1.5`, 'info');
                                                logEntry('AI_SELECT', `No AI manufacturer — fallback to DB manufacturer "${step2MfrName}"`, 'info', { dbManufacturerInfo });
                                            }
                                        }
                                    }
                                } // end: if (!rowSkipped) — Step 2b
                                } // end: Step 2 enabled check
                            }
                        } // end: step1b else (validated results approved)
                    }
                    } // end step1 enabled check

                    await checkMidStep(); // Pause/Stop check between Step 2 → Step 2.5
                    // ═══ STEP 2.5: MANUFACTURER VALIDATION & ROUTING ══════════
                    if (!rowSkipped && !descriptionOnlyMode && bestUrl && step2MfrName && isStepEnabled('step2_5')) {
                        addLog(`  🏭 Step 2.5: Manufacturer Validation...`, 'step');
                        logEntry('MFR_VALIDATE', `Validating manufacturer "${step2MfrName}"`, 'info', { manufacturer: step2MfrName, partNumber: step2MfrPN, scrapableUrl: bestUrl });
                        try {
                            const valResp = await apiClient.stepManufacturerValidate({
                                scrapableUrl: bestUrl,
                                manufacturerName: step2MfrName,
                                manufacturerPartNumber: step2MfrPN
                            });

                            if (valResp.success) {
                                // Show validation details for preview/approval
                                const step25Decision = await waitForApproval(
                                    'Manufacturer Validation', 2.5,
                                    {
                                        type: 'manufacturer_validation',
                                        scrapableUrl: bestUrl,
                                        manufacturerName: step2MfrName,
                                        partNumber: step2MfrPN,
                                        action: valResp.action,
                                        source: valResp.source,
                                        dataSource: valResp.dataSource,
                                        domainMatch: valResp.domainMatch,
                                        homepageDetected: valResp.homepageDetected || false,
                                        dbMatch: valResp.dbMatch,
                                        resultUrl: valResp.resultUrl,
                                        manufacturerUrl: valResp.manufacturerUrl,
                                        manufacturerInfo: valResp.manufacturerInfo,
                                        steps: valResp.steps
                                    },
                                    { rowNum, identifier }
                                );

                                if (step25Decision === 'skip') {
                                    addLog(`  ⏭ Row skipped by user`, 'info');
                                    rowSkipped = true; skipReason = 'user';
                                } else {
                                    dataSource = valResp.dataSource || 'Vendor URL';

                                    if (valResp.action === 'REROUTE' && valResp.resultUrl) {
                                        addLog(`  🔄 Rerouting to manufacturer URL: ${valResp.resultUrl}`, 'success');
                                        bestUrl = valResp.resultUrl;
                                        dataSource = 'Manufacturer URL';
                                        logEntry('MFR_VALIDATE', `Rerouted to manufacturer URL`, 'success', {
                                            originalUrl: valResp.scrapableUrl,
                                            resultUrl: valResp.resultUrl,
                                            manufacturer: step2MfrName,
                                            source: valResp.source
                                        });
                                    } else if (valResp.action === 'HOMEPAGE_FALLBACK') {
                                        // 1st link is only a homepage → scrape the other ranked link instead
                                        // (even if it was skipped by the 90% rule, since the 1st link is unusable)
                                        if (fallbackUrl && fallbackUrl !== bestUrl) {
                                            const homepageUrl = bestUrl;
                                            const fallbackLink = [scrapePlanInfo?.secondary, scrapePlanInfo?.skipped].find(l => l?.url === fallbackUrl);
                                            addLog(`  ⚠️ 1st link is only a homepage (${homepageUrl}) — scraping the other ranked link instead: ${fallbackUrl} (${labelScrapeUrl(fallbackUrl, scrapePlanInfo)})`, 'warning');
                                            bestUrl = fallbackUrl;
                                            secondaryUrl = null;
                                            dataSource = fallbackLink?.role === 'manufacturer' ? 'Manufacturer URL' : 'Vendor URL';
                                            logEntry('MFR_VALIDATE', 'Homepage fallback: switched to the other ranked link', 'warning', {
                                                homepageUrl, newUrl: bestUrl, newUrlRank: labelScrapeUrl(bestUrl, scrapePlanInfo),
                                                manufacturer: step2MfrName
                                            });
                                        } else {
                                            addLog(`  ⚠️ Manufacturer URL is a homepage (${bestUrl}) — no other ranked link available, continuing with homepage`, 'warning');
                                            dataSource = 'Manufacturer URL';
                                            logEntry('MFR_VALIDATE', `Homepage detected, no other ranked link`, 'warning', {
                                                homepageUrl: bestUrl, manufacturer: step2MfrName
                                            });
                                        }
                                    } else if (valResp.domainMatch) {
                                        addLog(`  ✅ URL already from manufacturer domain`, 'success');
                                        dataSource = 'Manufacturer URL';
                                        logEntry('MFR_VALIDATE', `Domain matches manufacturer`, 'success', { manufacturer: step2MfrName });
                                    } else {
                                        addLog(`  ℹ️ No manufacturer match in DB — using vendor URL`, 'info');
                                        dataSource = 'Vendor URL';
                                        logEntry('MFR_VALIDATE', `No manufacturer match, continuing with vendor URL`, 'info', {
                                            manufacturer: step2MfrName,
                                            dbMatch: valResp.dbMatch,
                                            steps: valResp.steps
                                        });
                                    }
                                    await saveStage('manufacturer_validate', { sourceUrl: bestUrl, secondaryUrl, dataSource });
                                }
                            } else {
                                addLog(`  ⚠️ Step 2.5 failed: ${valResp.error} — continuing with current URL`, 'warning');
                                logEntry('MFR_VALIDATE', `Validation failed: ${valResp.error}`, 'warning');
                            }
                        } catch (valErr) {
                            addLog(`  ⚠️ Step 2.5 error: ${valErr.message} — continuing with current URL`, 'warning');
                            logEntry('MFR_VALIDATE', `Error: ${valErr.message}`, 'error');
                        }
                    } else if (!rowSkipped && !descriptionOnlyMode && bestUrl && !step2MfrName) {
                        addLog(`  ℹ️ Step 2.5: Skipped — no manufacturer name from classification`, 'info');
                    }

                    // ── TIER 3 BRANCH: Description-only AI extraction ────────
                    // Description-only mode: rows proceed to Step 5 (dynamic prompt) using description as content
                    if (!rowSkipped && descriptionOnlyMode) {
                        addLog(`  ℹ️ Description-only mode — will use description as content for dynamic prompt generation`, 'info');
                    }

                    let holdMode = false;
                    let specContent = null;

                    if (!rowSkipped && !descriptionOnlyMode && isStepEnabled('step3')) {
                        await checkMidStep(); // Pause/Stop check before Step 3
                        // ═══ STEP 3: SCRAPE (Firecrawl) — Parallel ═══════════════
                        const urlsToScrape = [bestUrl, secondaryUrl].filter(Boolean);
                        setCurrentStepName('Step 3: Web Scraping');
                        addLog(`  📄 Step 3: Scraping ${urlsToScrape.length} URL(s) with Firecrawl${urlsToScrape.length > 1 ? ' (parallel)' : ''}...`, 'step');
                        // Scrape plan from Step 2: manufacturer first; vendor too only if manufacturer confidence is 90% or lower
                        const notScrapedLink = !secondaryUrl ? scrapePlanInfo?.skipped || null : null;
                        if (notScrapedLink && notScrapedLink.url !== bestUrl) {
                            addLog(`  ⏭ 2nd link not scraped: ${labelScrapeUrl(notScrapedLink.url, scrapePlanInfo)} — ${notScrapedLink.reason}`, 'detail');
                        }
                        logEntry('SCRAPE', urlsToScrape.length > 1 ? 'Scraping 1st and 2nd ranked links' : 'Scraping 1st ranked link only', 'info', {
                            firstUrl: bestUrl,
                            firstLink: labelScrapeUrl(bestUrl, scrapePlanInfo),
                            secondUrl: secondaryUrl || null,
                            secondLink: secondaryUrl ? labelScrapeUrl(secondaryUrl, scrapePlanInfo) : null,
                            notScraped: notScrapedLink && notScrapedLink.url !== bestUrl
                                ? { url: notScrapedLink.url, role: notScrapedLink.role, confidence: notScrapedLink.confidence, reason: notScrapedLink.reason }
                                : null,
                            rule: scrapePlanInfo?.rule || null,
                        });

                        let primaryContent = '';
                        let secondaryContent = '';

                        // ── Fire all scrapes in parallel ──
                        const scrapeStartTime = performance.now();
                        addLog(`  📤 Step 3a: Sending 1st link to Firecrawl — ${labelScrapeUrl(urlsToScrape[0], scrapePlanInfo)}...`, 'step');
                        addLog(`    ↳ ${urlsToScrape[0]}`, 'detail');

                        const deepClean = isStepEnabled('step3_5');
                        if (!deepClean) addLog(`  ⏭️ Step 3.5: Content Deep Clean — SKIPPED (disabled in settings)`, 'warning');
                        const scrapePromises = [apiClient.stepScrape({ url: urlsToScrape[0], deepClean })];
                        if (urlsToScrape.length > 1) {
                            addLog(`  📤 Step 3b: Sending 2nd link to Firecrawl (parallel) — ${labelScrapeUrl(urlsToScrape[1], scrapePlanInfo)}...`, 'step');
                            addLog(`    ↳ ${urlsToScrape[1]}`, 'detail');
                            scrapePromises.push(apiClient.stepScrape({ url: urlsToScrape[1], deepClean }));
                        }

                        const scrapeResults = await Promise.allSettled(scrapePromises);
                        const scrapeElapsed = ((performance.now() - scrapeStartTime) / 1000).toFixed(1);
                        addLog(`  ⏱ Scraping completed in ${scrapeElapsed}s`, 'info');

                        // ── Process primary result ──
                        const primarySettled = scrapeResults[0];
                        if (primarySettled.status === 'fulfilled' && primarySettled.value.success) {
                            const primaryResult = primarySettled.value;
                            if (primaryResult.holdRequired) {
                                addLog(`  ⏸ Primary content exceeds limit (${primaryResult.filteredCharCount}/${primaryResult.charLimit} chars) → HOLD`, 'warning');
                                logEntry('SCRAPE', `Primary content exceeds limit → HOLD`, 'hold', {
                                    url: bestUrl, filteredCharCount: primaryResult.filteredCharCount, charLimit: primaryResult.charLimit
                                });
                                primaryContent = primaryResult.cleanedContent;
                                holdMode = true;
                            } else {
                                primaryContent = primaryResult.cleanedContent;
                                addLog(`  ✅ Primary scraped ${primaryContent.length} chars from ${urlsToScrape[0]}`, 'success');
                                logEntry('SCRAPE', `Primary scraped ${primaryContent.length} chars`, 'success', { url: urlsToScrape[0], chars: primaryContent.length });
                                if (primaryResult.deepCleanStats) {
                                    const ds = primaryResult.deepCleanStats;
                                    addLog(`  🧹 Step 3.5: Code-based cleaning — junk:${ds.junkRemoved} commercial:${ds.commercialRemoved} dupes:${ds.duplicatesRemoved} kvDupes:${ds.kvDuplicatesRemoved} words:${ds.repeatedWordsRemoved} → ${ds.reductionPercent}% reduction`, 'info');
                                    logEntry('DEEP_CLEAN', `Code-based cleaning: ${ds.reductionPercent}% reduction`, 'success', ds);
                                }
                            }
                        } else {
                            const errMsg = primarySettled.status === 'rejected'
                                ? primarySettled.reason?.message
                                : primarySettled.value?.error || 'Unknown error';
                            addLog(`  ❌ Primary scrape failed: ${errMsg}`, 'error');
                            logEntry('SCRAPE', `Primary scrape failed`, 'error', { url: urlsToScrape[0], error: errMsg });
                        }

                        // Show primary result preview
                        if (primaryContent && !holdMode) {
                            const step3aDecision = await waitForApproval(
                                'Primary Scraped Content (Step 3a)', 3,
                                {
                                    type: 'scraped_content',
                                    url: urlsToScrape[0],
                                    label: 'Primary URL',
                                    contentLength: primaryContent.length,
                                    filteredCharCount: (primaryContent.match(/[A-Za-z0-9]/g) || []).length,
                                    preview: primaryContent,
                                    hasSecondary: urlsToScrape.length > 1
                                },
                                { rowNum, identifier }
                            );
                            if (step3aDecision === 'skip') {
                                addLog(`  ⏭ Row skipped by user`, 'info');
                                logEntry('SCRAPE', 'Skipped by user at primary', 'skipped');
                                rowSkipped = true; skipReason = 'user';
                            } else if (typeof step3aDecision === 'string' && step3aDecision !== 'approve') {
                                primaryContent = step3aDecision;
                                addLog(`  ✏️ Primary content edited by user (${primaryContent.length} chars)`, 'detail');
                            }
                        }

                        // ── Process secondary result ──
                        if (!rowSkipped && scrapeResults.length > 1) {
                            const secondarySettled = scrapeResults[1];
                            if (secondarySettled.status === 'fulfilled' && secondarySettled.value.success) {
                                secondaryContent = secondarySettled.value.cleanedContent || '';
                                addLog(`  ✅ Secondary scraped ${secondaryContent.length} chars from ${urlsToScrape[1]}`, 'success');
                                logEntry('SCRAPE', `Secondary scraped ${secondaryContent.length} chars`, 'success', { url: urlsToScrape[1], chars: secondaryContent.length });
                                if (secondarySettled.value.deepCleanStats) {
                                    const ds = secondarySettled.value.deepCleanStats;
                                    addLog(`  🧹 Step 3.5: Secondary cleaning — junk:${ds.junkRemoved} commercial:${ds.commercialRemoved} dupes:${ds.duplicatesRemoved} → ${ds.reductionPercent}% reduction`, 'info');
                                    logEntry('DEEP_CLEAN', `Secondary cleaning: ${ds.reductionPercent}% reduction`, 'success', ds);
                                }
                            } else {
                                const errMsg = secondarySettled.status === 'rejected'
                                    ? secondarySettled.reason?.message
                                    : secondarySettled.value?.error || 'Unknown error';
                                addLog(`  ⚠️ Secondary scrape failed: ${errMsg} — continuing with primary only`, 'warning');
                                logEntry('SCRAPE', `Secondary scrape failed`, 'warning', { url: urlsToScrape[1], error: errMsg });
                            }

                            // Show secondary result preview
                            if (secondaryContent) {
                                const step3bDecision = await waitForApproval(
                                    'Secondary Scraped Content (Step 3b)', 3,
                                    {
                                        type: 'scraped_content',
                                        url: urlsToScrape[1],
                                        label: 'Secondary URL',
                                        contentLength: secondaryContent.length,
                                        filteredCharCount: (secondaryContent.match(/[A-Za-z0-9]/g) || []).length,
                                        preview: secondaryContent
                                    },
                                    { rowNum, identifier }
                                );
                                if (step3bDecision === 'skip') {
                                    addLog(`  ⏭ Secondary content skipped by user — using primary only`, 'info');
                                    secondaryContent = '';
                                } else if (typeof step3bDecision === 'string' && step3bDecision !== 'approve') {
                                    secondaryContent = step3bDecision;
                                    addLog(`  ✏️ Secondary content edited by user (${secondaryContent.length} chars)`, 'detail');
                                }
                            }
                        }

                        if (!primaryContent && !secondaryContent) {
                            addLog(`  ⚠️ All scraping failed — falling back to description-only mode`, 'warning');
                            logEntry('SCRAPE', 'All scraping failed — fallback to description-only', 'warning', { primaryUrl: bestUrl, secondaryUrl });
                            descriptionOnlyMode = true;
                        } else if (holdMode) {
                            scrapedContent = secondaryContent
                                ? primaryContent + '\n\n=== SECONDARY SOURCE ===\n' + secondaryContent
                                : primaryContent;
                        } else if (!rowSkipped) {
                            scrapedContent = secondaryContent
                                ? primaryContent + '\n\n=== SECONDARY SOURCE ===\n' + secondaryContent
                                : primaryContent;

                            addLog(`  ✅ Step 3 Complete: Total ${scrapedContent.length} chars${secondaryContent ? ' (combined from 2 sources)' : ''}`, 'success');
                            await saveStage('scrape', { sourceUrl: bestUrl, secondaryUrl, scrapedContent });
                            logEntry('SCRAPE', `Scraped ${scrapedContent.length} chars total`, 'success', {
                                primaryUrl: bestUrl,
                                secondaryUrl: secondaryUrl || null,
                                primaryLength: primaryContent.length,
                                secondaryLength: secondaryContent.length,
                                combinedLength: scrapedContent.length
                            });
                        }
                    }

                    await checkMidStep(); // Pause/Stop check before Step 4
                    // ═══ STEP 4: AI SPEC EXTRACTION (Gemini 2.5 Flash Lite) ═══
                    if (!rowSkipped && !descriptionOnlyMode && !holdMode && scrapedContent && isStepEnabled('step4')) {
                        setCurrentStepName('Step 4: AI Spec Extraction');
                        addLog(`  🧹 Step 4: AI Spec Extraction...`, 'step');
                        logEntry('SPEC_EXTRACT', 'Starting AI spec extraction', 'info', { contentLength: scrapedContent.length });
                        const cleanExtResp = await apiClient.stepCleanExtract({ scrapedContent, model: getSpecExtractModel() });
                        if (!cleanExtResp.success) {
                            addLog(`  ❌ Spec extraction failed: ${cleanExtResp.error}`, 'error');
                            addLog(`  ⚠️ Falling back to raw scraped content`, 'warning');
                        } else {
                            specContent = cleanExtResp.specContent;
                            const step4Decision = await waitForApproval(
                                'AI Spec Extraction',
                                4,
                                {
                                    type: 'spec_extraction',
                                    specContent: specContent,
                                    originalLength: cleanExtResp.originalLength,
                                    cleanedLength: cleanExtResp.cleanedLength,
                                    specLength: cleanExtResp.specLength,
                                    preview: specContent.substring(0, 6000)
                                },
                                { rowNum, identifier }
                            );
                            if (step4Decision === 'skip') { rowSkipped = true; skipReason = 'user'; }
                            else {
                                addLog(`  ✅ Specs extracted: ${cleanExtResp.originalLength} → ${cleanExtResp.specLength} chars`, 'success');
                                logEntry('SPEC_EXTRACT', `Extracted specs (${cleanExtResp.specLength} chars)`, 'success', {
                                    originalLength: cleanExtResp.originalLength,
                                    cleanedLength: cleanExtResp.cleanedLength,
                                    specLength: cleanExtResp.specLength,
                                    specContent
                                });
                                await saveStage('spec_extract', {});
                            }
                        }
                    }

                    // ═══ STEP 2.5 FALLBACK: Re-run if Step 4 extracted manufacturer info ═══
                    // When Step 2 didn't provide manufacturer info, but Step 4 AI extraction did
                    if (!rowSkipped && !descriptionOnlyMode && !holdMode && specContent && !step2MfrName && isStepEnabled('step2_5')) {
                        // Try to extract manufacturer name and part number from the spec content
                        const mfrNameMatch = specContent.match(/(?:manufacturer|brand|made by|produced by)[:\s]+([A-Za-z][A-Za-z0-9\s&.-]{1,40})/i);
                        const mfrPNMatch = specContent.match(/(?:manufacturer part|mfr part|mpn|oem part)[^:]*[:\s]+([A-Za-z0-9\-/.]{3,40})/i);
                        const extractedMfrName = mfrNameMatch?.[1]?.trim() || '';
                        const extractedMfrPN = mfrPNMatch?.[1]?.trim() || mfrPN || splPN || '';

                        if (extractedMfrName) {
                            addLog(`  🏭 Step 2.5 (Fallback): Found manufacturer "${extractedMfrName}" in specs — validating...`, 'step');
                            try {
                                const fallbackResp = await apiClient.stepManufacturerValidate({
                                    scrapableUrl: bestUrl,
                                    manufacturerName: extractedMfrName,
                                    manufacturerPartNumber: extractedMfrPN
                                });

                                if (fallbackResp.success && fallbackResp.action === 'REROUTE' && fallbackResp.resultUrl) {
                                    addLog(`  🔄 Manufacturer URL found: ${fallbackResp.resultUrl}`, 'success');
                                    addLog(`  📄 Scraping manufacturer page...`, 'step');

                                    const mfrScrapeResp = await apiClient.stepScrape({ url: fallbackResp.resultUrl, deepClean: isStepEnabled('step3_5') });
                                    if (mfrScrapeResp.success && mfrScrapeResp.cleanedContent) {
                                        const mfrExtResp = await apiClient.stepCleanExtract({
                                            scrapedContent: mfrScrapeResp.cleanedContent,
                                            model: getSpecExtractModel()
                                        });

                                        if (mfrExtResp.success && mfrExtResp.specContent) {
                                            const vendorSpecs = specContent;
                                            specContent = `=== VENDOR SPECIFICATIONS ===\n${vendorSpecs}\n\n=== MANUFACTURER SPECIFICATIONS ===\n${mfrExtResp.specContent}`;
                                            dataSource = 'Both';
                                            addLog(`  ✅ Merged vendor + manufacturer specs (${vendorSpecs.length} + ${mfrExtResp.specContent.length} chars)`, 'success');
                                            logEntry('MFR_FALLBACK', `Merged vendor + manufacturer specs`, 'success', {
                                                manufacturerName: extractedMfrName,
                                                manufacturerUrl: fallbackResp.resultUrl,
                                                vendorSpecLength: vendorSpecs.length,
                                                mfrSpecLength: mfrExtResp.specContent.length,
                                                dataSource: 'Both'
                                            });

                                            if (!classifiedMfrs.some(m => m.name.toLowerCase() === extractedMfrName.toLowerCase())) {
                                                classifiedMfrs.push({ name: extractedMfrName, url: fallbackResp.resultUrl, partNumber: extractedMfrPN });
                                            }
                                            await saveStage('manufacturer_fallback', { dataSource, sourceUrl: bestUrl });
                                        } else {
                                            addLog(`  ⚠️ Manufacturer spec extraction failed — using vendor specs only`, 'warning');
                                        }
                                    } else {
                                        addLog(`  ⚠️ Manufacturer page scrape failed — using vendor specs only`, 'warning');
                                    }
                                } else if (fallbackResp.success && fallbackResp.domainMatch) {
                                    addLog(`  ✅ Step 2.5 (Fallback): URL is already from "${extractedMfrName}" domain`, 'success');
                                    dataSource = 'Manufacturer URL';
                                } else {
                                    addLog(`  ℹ️ Step 2.5 (Fallback): "${extractedMfrName}" not found in manufacturer DB`, 'info');
                                }
                            } catch (fbErr) {
                                addLog(`  ⚠️ Step 2.5 (Fallback) error: ${fbErr.message}`, 'warning');
                            }
                        }
                    }

                    await checkMidStep(); // Pause/Stop check before Step 4.5
                    // ═══ STEP 4.5: CLEAN NOUN/MODIFIER FROM EXTRACTED CONTENT ═══
                    if (!rowSkipped && !descriptionOnlyMode && !holdMode && specContent && isStepEnabled('step4_5')) {
                        const nm = (row.nounModifier || '').trim();
                        if (nm) {
                            addLog(`  🧼 Step 4.5: Removing Noun/Modifier "${nm}" from spec content...`, 'step');
                            const beforeLen = specContent.length;
                            // Split Noun/Modifier by comma to handle values like "BEARING, BALL"
                            const nmParts = nm.split(/[,\/]/).map(p => p.trim()).filter(Boolean);
                            let cleaned = specContent;
                            for (const part of nmParts) {
                                // Exact word boundary match, case-insensitive, global
                                const escaped = part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                const regex = new RegExp(`\\b${escaped}\\b`, 'gi');
                                cleaned = cleaned.replace(regex, '').replace(/\s{2,}/g, ' ');
                            }
                            // Also remove the full Noun/Modifier as-is (e.g. "BEARING, BALL" as one phrase)
                            if (nmParts.length > 1) {
                                const escapedFull = nm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                const fullRegex = new RegExp(escapedFull, 'gi');
                                cleaned = cleaned.replace(fullRegex, '').replace(/\s{2,}/g, ' ');
                            }
                            cleaned = cleaned.trim();
                            specContent = cleaned;
                            const removed = beforeLen - cleaned.length;
                            if (removed > 0) {
                                addLog(`  ✅ Removed ${removed} char(s) of Noun/Modifier text (${beforeLen} → ${cleaned.length})`, 'success');
                            } else {
                                addLog(`  ℹ️ No Noun/Modifier text found in spec content`, 'info');
                            }
                            logEntry('NM_CLEAN', `Cleaned Noun/Modifier from spec content`, 'success', {
                                nounModifier: nm, charsBefore: beforeLen, charsAfter: cleaned.length, removed
                            });
                        }
                    }

                    // ═══ STEP 5: SKIPPED (AI Model handles mapping directly) ═══
                    // Property prompt generation removed — using trained AI model instead

                    if (!rowSkipped && !holdMode && isStepEnabled('step6')) {
                        await checkMidStep(); // Pause/Stop check before Step 6
                        // ═══ STEP 6: AI EXTRACTION (Engine-Routed) ═══════════════════
                        const engine = getExtractionEngine();
                        const engineLabel = engine === 'flan-t5' ? '🤖 Flan-T5' : engine === 'openai-luna' ? '🌙 GPT-5.6 Luna+Memory' : '✨ Gemini+Memory';
                        setCurrentStepName(`Step 6: ${engineLabel} Extraction`);
                        addLog(`  ${engine === 'flan-t5' ? '🤖' : engine === 'openai-luna' ? '🌙' : '✨'} Step 6: Extracting attributes via ${engineLabel}...`, 'step');
                        logEntry('EXTRACT', `Sending to ${engineLabel} for attribute extraction`, 'info', { attributeCount: attributeLines.length, engine });

                        let extractResp;
                        if (engine === 'flan-t5') {
                            // ── Flan-T5 Custom Model (with Gemini fallback) ──
                            try {
                                extractResp = await apiClient.stepModelExtract({
                                    cleanedContent: specContent || scrapedContent || description,
                                    attributeLines,
                                    description,
                                    nounModifier: (row.nounModifier || '').trim(),
                                });
                                if (!extractResp.success) {
                                    addLog(`  ⚠️ Flan-T5 unavailable: ${extractResp.error || 'unknown'} — falling back to Gemini`, 'warning');
                                    extractResp = await apiClient.stepExtract({
                                        cleanedContent: specContent || scrapedContent || description,
                                        attributeLines,
                                        description,
                                        nounModifier: (row.nounModifier || '').trim(),
                                    });
                                }
                            } catch (modelErr) {
                                addLog(`  ⚠️ Flan-T5 failed: ${modelErr.message} — falling back to Gemini`, 'warning');
                                extractResp = await apiClient.stepExtract({
                                    cleanedContent: specContent || scrapedContent || description,
                                    attributeLines,
                                    description,
                                    nounModifier: (row.nounModifier || '').trim(),
                                });
                            }
                        } else {
                            // ── Gemini + Dynamic Few-Shot Memory ──
                            try {
                                extractResp = await apiClient.stepExtractWithMemory({
                                    cleanedContent: specContent || scrapedContent || description,
                                    attributeLines,
                                    description,
                                    nounModifier: (row.nounModifier || '').trim(),
                                    manufacturerName: step2MfrName || '',
                                    provider: engine === 'openai-luna' ? 'openai' : 'gemini',
                                    model: engine === 'openai-luna' ? 'gpt-5.6-luna' : getGeminiExtractionModel(),
                                    conditionModel: getConditionGenerationModel(),
                                });
                                if (extractResp.success) {
                                    const tierEmoji = { tier1_both: '🎯', tier2_partial: '⚡', tier2_nm_only: '📂', tier2_mfr_only: '🏭', tier3_default: '🔲' };
                                    addLog(`  📚 Few-shot: ${extractResp.fewShotExamplesUsed || 0} examples (${tierEmoji[extractResp.matchTier] || '❓'} ${extractResp.matchTier || 'unknown'})`, 'info');
                                    // Step 6 — Missing Property Conditions: rules from the DB and rules the AI wrote
                                    if (extractResp.conditionMemory) {
                                        const conditionLog = describeConditionMemory(extractResp.conditionMemory, { conditionModel: getConditionGenerationModel() });
                                        conditionLog.lines.forEach(([message, level]) => addLog(message, level));
                                        logEntry('CONDITION_MEMORY', conditionLog.action, conditionLog.result, conditionLog.data);
                                    }
                                }
                            } catch (memErr) {
                                addLog(`  ⚠️ Gemini+Memory failed: ${memErr.message} — trying plain Gemini`, 'warning');
                                logEntry('EXTRACT', `Gemini+Memory error: ${memErr.message} — falling back`, 'warning', { error: memErr.message });
                                extractResp = await apiClient.stepExtract({
                                    cleanedContent: specContent || scrapedContent || description,
                                    attributeLines,
                                    description,
                                    nounModifier: (row.nounModifier || '').trim(),
                                });
                            }
                        }

                        const provider = extractResp.provider || 'gemini';
                        const providerIcon = provider === 'local-model' ? '🤖' : provider === 'gemini-fewshot' ? '✨' : '☁️';

                        if (!extractResp.success) {
                            addLog(`  ❌ Extraction failed (${provider}): ${extractResp.error}`, 'error');
                            logEntry('EXTRACT', `Extraction failed (${provider}): ${extractResp.error}`, 'error', { provider, error: extractResp.error });
                            rowSkipped = true; skipReason = 'auto';
                        } else {
                            extractedAttrs = extractResp.attributes;
                            mappedAttrs = extractResp.mapped;
                            addLog(`  ${providerIcon} Extracted via ${provider} (model: ${extractResp.extractionModel || 'default'})`, 'success');
                            logEntry('EXTRACT', `Extracted via ${provider} (model: ${extractResp.extractionModel || 'default'})`, 'success', {
                                attributeCount: extractResp.attributeCount,
                                provider,
                                model: extractResp.extractionModel,
                                fewShotExamplesUsed: extractResp.fewShotExamplesUsed || 0,
                            });


                            // ── Step 6.5 (Custom Code): remove extracted values that exactly match the Part Number or Noun/Modifier ──
                            if (isStepEnabled('step6_5') && extractedAttrs) {
                                const cleanupInput = {
                                    partNumbers: [row.partNumber, mfrPN, splPN, row.supplierPartNumber2, row.supplierPartNumber3, row.supplierPartNumber4],
                                    nounModifier: row.nounModifier || '',
                                };
                                const cleanup = removePartNumberAndNounModifier(extractedAttrs, cleanupInput);
                                extractedAttrs = cleanup.data;
                                if (cleanup.removed.length > 0) {
                                    addLog(`  🧼 Step 6.5 Custom Code: removed ${cleanup.removed.length} value(s) matching the Part Number / Noun/Modifier`, 'info');
                                    cleanup.removed.forEach(r => addLog(`     − ${r.field}: "${r.value}" (${r.matched})`, 'detail'));
                                } else {
                                    addLog(`  🧼 Step 6.5 Custom Code: no Part Number / Noun/Modifier values found in extracted data`, 'detail');
                                }
                                logEntry('CUSTOM_CLEAN', cleanup.removed.length > 0
                                    ? `Removed ${cleanup.removed.length} value(s) matching the Part Number / Noun/Modifier`
                                    : 'No Part Number / Noun/Modifier values in extracted data', 'success', {
                                    partNumbers: cleanup.terms.partNumbers,
                                    nounModifier: cleanup.terms.nounModifier,
                                    removed: cleanup.removed,
                                    extractedData: extractedAttrs,
                                });
                            } else if (!isStepEnabled('step6_5')) {
                                addLog(`  ⏭️ Step 6.5: Custom Code cleanup — SKIPPED (disabled in settings)`, 'warning');
                            }

                            const step6Decision = await waitForApproval(
                                'Extracted Attributes',
                                6,
                                {
                                    type: 'extracted_attributes',
                                    attributes: extractedAttrs,
                                    mapped: mappedAttrs,
                                    attributeCount: extractResp.attributeCount
                                },
                                { rowNum, identifier }
                            );

                            if (step6Decision === 'skip') {
                                addLog(`  ⏭ Row skipped by user`, 'info');
                                logEntry('EXTRACT', 'Skipped by user', 'skipped');
                                rowSkipped = true; skipReason = 'user';
                            } else {
                                addLog(`  ✅ Extracted ${extractResp.attributeCount} attribute(s)`, 'success');
                                logEntry('EXTRACT', `Extracted ${extractResp.attributeCount} attribute(s)`, 'success', {
                                    attributeCount: extractResp.attributeCount,
                                    extractedAttrs,
                                    mappedAttrs,
                                    fullPrompt: extractResp.fullPrompt || ''
                                });
                                await saveStage('extract', {
                                    chatgptOutput: JSON.stringify(injectManufacturerMeta(extractedAttrs, row)),
                                    chatgptSuccess: true,
                                    chatgptRaw: JSON.stringify(mappedAttrs),
                                    chatgptPrompt: extractResp.fullPrompt || '',
                                });
                            }
                        }
                    }

                    // ── Final state update to IndexedDB ──
                    const isSuccess = !rowSkipped && !holdMode && extractedAttrs;
                    let rowState = 'failed';
                    if (isSuccess) rowState = 'completed';
                    else if (holdMode) rowState = 'hold';
                    else if (rowSkipped && skipReason === 'user') rowState = 'skipped';
                    else if (rowSkipped && skipReason === 'no_match') rowState = 'no_match'; // no exact part number match
                    else if (rowSkipped) rowState = 'failed'; // auto-skip due to pipeline failure

                    if (rowState === 'completed') logEntry('COMPLETE', 'Pipeline completed successfully', 'success');
                    else if (rowState === 'hold') logEntry('COMPLETE', `Content held for manual review (${scrapedContent?.length || 0} chars)`, 'hold');
                    else if (rowState === 'skipped') logEntry('COMPLETE', 'Row skipped', 'error');
                    else if (rowState === 'no_match') logEntry('COMPLETE', 'No Match Found — exited: no link shows the exact part number', 'error');
                    else logEntry('COMPLETE', 'Pipeline failed', 'error');

                    // Final incremental save with completed state
                    // Inject manufacturer/supplier metadata from input row into AI output
                    const finalAttrs = isSuccess ? injectManufacturerMeta(extractedAttrs, {
                        ...row,
                        classifiedManufacturers: classifiedMfrs,
                        classifiedVendors: classifiedVendors,
                        sourceUrl: bestUrl,
                        secondaryUrl: secondaryUrl,
                    }) : null;

                    // ── Auto-capture training data: ScrapedContent (X) → PropertyValues (Y) ──
                    if (isSuccess && finalAttrs && scrapedContent) {
                        try {
                            await apiClient.saveTrainingPair({
                                scrapedContent: scrapedContent,
                                description: description || '',
                                description1,
                                description2,
                                nounModifier: (row.nounModifier || '').trim(),
                                propertyNames: attributeLines || [],
                                mappedOutput: finalAttrs,
                                manufacturerPn: row.manufacturerPartNumber || row['MANUFACTURER PART NUMBER 1'] || '',
                                manufacturerName: step2MfrName || '',
                                sourceUrl: bestUrl || null,
                                provider: extractResp?.provider || 'gemini',
                            });
                            if (extractResp?.conditionMemory?.drafts?.length) {
                                try {
                                    await apiClient.savePropertyConditions({
                                        conditions: extractResp.conditionMemory.drafts,
                                        nounModifier: (row.nounModifier || '').trim(),
                                        manufacturerName: step2MfrName || '',
                                    });
                                    addLog(`  Conditions saved automatically with completed data (${extractResp.conditionMemory.drafts.length})`, 'detail');
                                    logEntry('CONDITION_MEMORY', `Saved ${extractResp.conditionMemory.drafts.length} AI-written rule(s) to Property Conditions`, 'success', {
                                        saved: extractResp.conditionMemory.drafts.map(d => ({ property: d.propertyName, condition: d.conditionText })),
                                    });
                                } catch (conditionSaveError) {
                                    addLog(`  Data saved, but condition memory save failed: ${conditionSaveError.message}`, 'warning');
                                    logEntry('CONDITION_MEMORY', 'Product data saved, but saving the AI-written rules failed', 'warning', { error: conditionSaveError.message });
                                }
                            }
                            addLog(`  📊 Training data captured`, 'detail');
                        } catch (trainErr) {
                            console.warn('[TrainingData] Capture failed (non-blocking):', trainErr.message);
                        }
                    }

                    await saveStage('complete', {
                        chatgptOutput: finalAttrs ? JSON.stringify(finalAttrs) : null,
                        chatgptSuccess: isSuccess,
                        chatgptRaw: isSuccess ? JSON.stringify(mappedAttrs) : null,
                        chatgptError: rowState === 'no_match' ? 'No Match Found' : rowState === 'skipped' ? 'Skipped by user' : (rowState === 'hold' ? 'Content size limit exceeded' : (rowState === 'failed' ? (skipReason === 'auto' ? 'Pipeline step failed' : 'Pipeline failed') : (isSuccess ? null : 'Pipeline failed'))),
                        sourceType: descriptionOnlyMode ? 'Description' : 'Online',
                        sourceUrl: bestUrl || null,
                        dataSource,
                        reviewStatus: rowState === 'completed' ? 'PENDING' : rowState === 'no_match' ? 'NO_MATCH' : (rowState === 'hold' ? 'HOLD' : 'REVIEW'),
                        generatedPrompt: null,
                        promptProperties: attributeLines || [],
                        classifiedManufacturers: classifiedMfrs,
                        classifiedVendors: classifiedVendors,
                        nounModifier: (row.nounModifier || '').trim(),
                        searchTier,
                        status: rowState,
                        scrapedContent: holdMode ? scrapedContent : null,
                    });

                    if (rowState === 'completed') {
                        addLog(`  ✅ Row complete!`, 'success');
                        setEnrichmentStats(prev => ({ ...prev, completed: prev.completed + 1 }));
                    } else if (rowState === 'hold') {
                        addLog(`  ⏸ Row held for review.`, 'warning');
                        setEnrichmentStats(prev => ({ ...prev, completed: prev.completed + 1 }));
                    } else if (rowState === 'skipped' || rowState === 'failed' || rowState === 'no_match') {
                        setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                    }

                } catch (err) {
                    // Handle pipeline control signals
                    if (err.message === PIPELINE_STOP) {
                        addLog('  🛑 Record interrupted — pipeline stopping.', 'warning');
                        // Save current state before breaking
                        if (dbId) {
                            try { await updateItem(dbId, { status: 'stopped', reviewStatus: 'REVIEW', pipelineStep: 'stopped' }); } catch (_) { }
                        }
                        break; // Exit the for-loop
                    }
                    if (err.message === PIPELINE_SKIP) {
                        addLog('  ⏭ Record skipped by user.', 'info');
                        if (dbId) {
                            try { await updateItem(dbId, { status: 'skipped', reviewStatus: 'REVIEW', pipelineStep: 'skipped', chatgptError: 'Skipped by user' }); } catch (_) { }
                        }
                        setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                    } else {
                        addLog(`  ❌ Error: ${err.message}`, 'error');
                        setEnrichmentStats(prev => ({ ...prev, failed: prev.failed + 1 }));
                        // Save error state to IndexedDB
                        await saveStage('error', {
                            chatgptOutput: null, chatgptSuccess: false, chatgptError: err.message,
                            reviewStatus: 'REVIEW', status: 'failed',
                        });
                    }
                }

                addLog('─'.repeat(60), 'divider');
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            addLog('\n💾 All records saved incrementally to IndexedDB.', 'success');

            if (stopRef.current) {
                addLog('\n🛑 Pipeline Stopped!', 'header');
                setEnrichmentStats(prev => {
                    addLog(`   Total: ${prev.total} | ✅ Success: ${prev.completed} | ⏭ Skipped/Failed: ${prev.failed} | ⏳ Remaining: ${prev.total - prev.completed - prev.failed}`, 'info');
                    return prev;
                });
                setPipelineState('stopped');
                toast.warning('Pipeline stopped. Completed records are preserved.');
            } else {
                addLog('\n🏁 Pipeline Complete!', 'header');
                setEnrichmentStats(prev => {
                    addLog(`   Total: ${prev.total} | ✅ Success: ${prev.completed} | ⏭ Skipped/Failed: ${prev.failed}`, 'info');
                    return prev;
                });
                setPipelineState('completed');
                toast.success('Pipeline completed! Redirecting to Results...');
                setTimeout(() => navigate('/results', { state: { reload: Date.now() } }), 2000);
            }
        } catch (error) {
            console.error('Pipeline error:', error);
            const errMsg = error?.message || (typeof error === 'string' ? error : JSON.stringify(error)) || 'Unknown error';
            addLog(`\n💥 Pipeline Error: ${errMsg}`, 'error');
            toast.error(`Pipeline failed: ${errMsg}`);
            setPipelineState('stopped');
        } finally {
            setApiTraceRecorder(null);
            setEnrichmentRunning(false);
            setStepReview(null);
        }
    };

    // ── Step Review Panel Renderers ──────────────────────────────────
    const renderStepData = () => {
        if (!stepReview) return null;
        const { data } = stepReview;

        if (data.type === 'db_match') {
            const entries = data.data ? Object.entries(data.data).filter(([, v]) => v !== null && v !== undefined) : [];
            return (
                <div className="space-y-3">
                    <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-3">
                        <p className="text-xs text-green-400 mb-1">🗃️ Part Number Found in Master DB</p>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-400">PN:</span>
                            <span className="text-sm text-white font-mono font-bold">{data.partNumber}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-400">Matched in:</span>
                            <span className="text-xs text-amber-300 font-mono">{data.matchedColumn}</span>
                        </div>
                    </div>

                    {entries.length > 0 && (
                        <div className="bg-slate-800 rounded-lg p-3">
                            <p className="text-xs text-slate-400 mb-2">Existing Data ({entries.length} fields)</p>
                            <div className="max-h-64 overflow-y-auto space-y-0.5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 #1e293b' }}>
                                {entries.map(([k, v]) => {
                                    const isEmpty = v === '' || v === 'null' || v === 'NULL';
                                    return (
                                        <div key={k} className="flex justify-between text-xs py-0.5 px-1 hover:bg-slate-700/50 rounded">
                                            <span className={`shrink-0 mr-2 ${isEmpty ? 'text-slate-600' : 'text-slate-500'}`}>{k}:</span>
                                            <span className={`font-mono text-right break-all ${isEmpty ? 'text-slate-600 italic' : 'text-slate-300'}`}>
                                                {isEmpty ? '—' : String(v).substring(0, 200)}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-2.5">
                        <p className="text-xs text-amber-300">
                            ✅ <strong>Approve</strong> = Use existing data (skip pipeline)<br />
                            ❌ <strong>Reject</strong> = Re-scrape with fresh data
                        </p>
                    </div>
                </div>
            );
        }

        if (data.type === 'search_input') {
            return (
                <div className="space-y-3">
                    <div className="bg-slate-800 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-1">Search Query</p>
                        <p className="text-sm text-cyan-300 font-mono">"{data.query}"</p>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-2">Input Parameters</p>
                        {Object.entries(data.searchInput).filter(([, v]) => v).map(([k, v]) => (
                            <div key={k} className="flex justify-between text-xs py-0.5">
                                <span className="text-slate-500">{k}:</span>
                                <span className="text-slate-300 font-mono">{Array.isArray(v) ? v.join(', ') : v}</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // ── Supplier-Validated Search Input Preview ──
        if (data.type === 'search_validated_input') {
            // Build the unquoted query for display
            const originalPNs = data.supplierPNPairs?.map(p => p.partNumber).concat(data.manufacturerPartNumber ? [data.manufacturerPartNumber] : []).filter(Boolean) || [];
            const unquotedQuery = [...new Set(originalPNs)].join(' ');
            return (
                <div className="space-y-3">
                    {/* Serper Query 1: PN search (unquoted) */}
                    <div className="bg-slate-800 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                            <p className="text-xs text-slate-400">🔍 Serper Query 1 — Part Number</p>
                            <span className="text-[10px] bg-cyan-900/50 text-cyan-400 px-1.5 py-0.5 rounded">1 credit</span>
                        </div>
                        <p className="text-sm text-cyan-300 font-mono break-all leading-relaxed">{unquotedQuery}</p>
                        <p className="text-[10px] text-slate-500 mt-1">Unquoted — Google auto-resolves variants (e.g. 800TX550 → 800T-X550)</p>
                    </div>
                    {/* Serper Query 2: Description search */}
                    {data.descriptionForSearch && (
                        <div className="bg-slate-800 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                                <p className="text-xs text-slate-400">📝 Serper Query 2 — Description</p>
                                <span className="text-[10px] bg-amber-900/50 text-amber-400 px-1.5 py-0.5 rounded">1 credit</span>
                            </div>
                            <p className="text-sm text-amber-300 font-mono break-all">{data.descriptionForSearch}</p>
                        </div>
                    )}
                    {data.descriptionSearchOn === false && (
                        <div className="bg-slate-800 rounded-lg p-3 border-l-2 border-amber-500">
                            <p className="text-xs text-amber-400 font-semibold">📝 Description search is OFF</p>
                            <p className="text-[11px] text-slate-400 mt-0.5">Serper gets the Part Number only (Pipeline settings). AI still picks the Manufacturer and Vendor websites from the results.</p>
                        </div>
                    )}
                    {data.supplierPNPairs?.length > 0 && (
                        <div className="bg-slate-800 rounded-lg p-3">
                            <p className="text-xs text-slate-400 mb-2">Supplier → Part Number Pairs</p>
                            {data.supplierPNPairs.map((p, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-slate-700/50 last:border-0">
                                    <span className="text-blue-400 font-medium">{p.supplier}</span>
                                    <span className="text-slate-500">→</span>
                                    <span className="text-emerald-300 font-mono">{p.partNumber}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="bg-slate-800 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-2">Other Parameters</p>
                        {data.description && (
                            <div className="flex justify-between text-xs py-0.5">
                                <span className="text-slate-500">description:</span>
                                <span className="text-slate-300 font-mono text-right max-w-[70%] truncate">{data.description}</span>
                            </div>
                        )}
                        {data.manufacturerPartNumber && (
                            <div className="flex justify-between text-xs py-0.5">
                                <span className="text-slate-500">manufacturerPN:</span>
                                <span className="text-slate-300 font-mono">{data.manufacturerPartNumber}</span>
                            </div>
                        )}
                        {data.companyName && (
                            <div className="flex justify-between text-xs py-0.5">
                                <span className="text-slate-500">companyName:</span>
                                <span className="text-slate-300 font-mono">{data.companyName}</span>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // ── Supplier-Validated Search Results Preview ──
        if (data.type === 'search_validated_results') {
            const vReport = data.validationReport || {};
            const perPn = vReport.perPnValidation || [];
            const discarded = vReport.discardedPNs || [];
            return (
                <div className="space-y-3">
                    {/* Summary bar */}
                    <div className="bg-slate-800 rounded-lg p-3 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-slate-400">Total Results</p>
                            <p className="text-lg text-white font-bold">{data.totalResults}</p>
                        </div>
                        <div className="flex gap-3 text-xs">
                            <span className="text-emerald-400">PN: {data.searchSources?.partNumber || 0}</span>
                            <span className="text-blue-400">Desc: {data.searchSources?.description || 0}</span>
                            {discarded.length > 0 && <span className="text-red-400">Discarded: {discarded.length}</span>}
                        </div>
                    </div>

                    {/* Per-PN Validation Cards */}
                    {perPn.length > 0 && (
                        <div className="space-y-2">
                            <p className="text-xs text-slate-400 font-medium">Supplier Validation</p>
                            {perPn.map((v, i) => (
                                <div key={i} className={`rounded-lg p-2.5 border ${v.matched ? 'bg-emerald-900/20 border-emerald-700/40' : 'bg-red-900/20 border-red-700/40'}`}>
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm">{v.matched ? '✅' : '❌'}</span>
                                            <span className="text-xs text-white font-mono font-bold">{v.partNumber}</span>
                                            <span className="text-xs text-slate-500">→</span>
                                            <span className="text-xs text-blue-300">{v.supplier}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            {v.fallbackUsed && <span className="text-[10px] bg-amber-600/30 text-amber-300 px-1.5 py-0.5 rounded">FALLBACK</span>}
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${v.matched ? 'bg-emerald-600/30 text-emerald-300' : 'bg-red-600/30 text-red-300'}`}>
                                                {v.matched ? `${v.matchedUrls?.length || 0} match${(v.matchedUrls?.length || 0) !== 1 ? 'es' : ''}` : 'DISCARDED'}
                                            </span>
                                        </div>
                                    </div>
                                    {v.matched && v.matchedUrls?.length > 0 && (
                                        <div className="mt-1 space-y-0.5">
                                            {v.matchedUrls.slice(0, 3).map((mu, j) => (
                                                <div key={j} className="flex items-center gap-1 text-[10px]">
                                                    <span className="text-emerald-400">↳</span>
                                                    <span className="text-slate-400 truncate max-w-[90%]">{mu.domain}</span>
                                                </div>
                                            ))}
                                            {v.matchedUrls.length > 3 && <span className="text-[10px] text-slate-500">+{v.matchedUrls.length - 3} more</span>}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* URL List */}
                    {data.results?.length > 0 && (
                        <div className="bg-slate-800 rounded-lg p-3">
                            <p className="text-xs text-slate-400 mb-2">Search Results ({data.results.length})</p>
                            <div className="max-h-48 overflow-y-auto space-y-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 #1e293b' }}>
                                {data.results.map((r, i) => (
                                    <div key={i} className="flex items-start gap-2 text-[11px] py-1 border-b border-slate-700/30 last:border-0">
                                        <span className={`mt-0.5 px-1 py-0.5 rounded text-[9px] font-bold ${r.searchSource === 'partNumber' ? 'bg-cyan-900/50 text-cyan-400' : r.searchSource === 'fallback' ? 'bg-amber-900/50 text-amber-400' : 'bg-purple-900/50 text-purple-400'}`}>
                                            {r.searchSource === 'partNumber' ? 'PN' : r.searchSource === 'fallback' ? 'FB' : 'DESC'}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <a href={r.link} target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 truncate block">{r.title}</a>
                                            <span className="text-slate-500 text-[10px]">{r.domain}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        if (data.type === 'url_selection') {
            return (
                <div className="space-y-3">
                    {/* Scrape plan: manufacturer first; vendor too only if manufacturer confidence is 90% or lower */}
                    {data.scrapePlan ? (
                        <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-3 space-y-2">
                            <p className="text-xs text-emerald-400 font-semibold">📄 Scrape plan ({data.selectionMethod || 'AI_SELECTED'})</p>
                            {[
                                { label: '1st — scrape', link: data.scrapePlan.primary, tone: 'text-emerald-300' },
                                { label: '2nd — scrape', link: data.scrapePlan.secondary, tone: 'text-emerald-300' },
                                { label: '2nd — not scraped', link: data.scrapePlan.skipped, tone: 'text-slate-400' },
                                ...(data.scrapePlan.rejected || []).map(link => ({ label: 'Not used', link, tone: 'text-red-300' })),
                            ].filter(row => row.link).map(row => (
                                <div key={`${row.label}-${row.link.url}`} className="text-xs bg-slate-900/40 rounded px-2 py-1.5">
                                    <div className={`font-semibold ${row.tone}`}>
                                        {row.label}: {row.link.role} · confidence {row.link.confidence}%
                                        {row.link.match && <span className={row.link.match.matched ? 'text-emerald-300' : 'text-red-300'}> · {describeMatch(row.link.match)}</span>}
                                        {row.link.reason && <span className="font-normal text-slate-400"> — {row.link.reason}</span>}
                                    </div>
                                    <a href={row.link.url} target="_blank" rel="noopener noreferrer"
                                        className="text-cyan-300 hover:text-cyan-200 break-all flex items-center gap-1 text-[11px]">
                                        {row.link.url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                    </a>
                                </div>
                            ))}
                            {!data.scrapePlan.primary && <p className="text-xs text-red-300">No link to scrape</p>}
                            <p className="text-[10px] text-slate-400">{data.scrapePlan.rule}</p>
                            {(data.notes || []).map((note, idx) => <p key={idx} className="text-[10px] text-amber-300">ℹ️ {note}</p>)}
                        </div>
                    ) : (
                        <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-3">
                            <p className="text-xs text-emerald-400 mb-1">✅ Scrapable URL ({data.selectionMethod || 'AI_SELECTED'})</p>
                            <a href={data.scrapable_url} target="_blank" rel="noopener noreferrer"
                                className="text-sm text-cyan-300 hover:text-cyan-200 break-all flex items-center gap-1">
                                {data.scrapable_url}
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                        </div>
                    )}

                    {/* Manufacturers (with name + URL) */}
                    {data.manufacturers && data.manufacturers.length > 0 ? (
                        <div className="bg-slate-800 rounded-lg p-3 border-l-2 border-blue-500">
                            <p className="text-xs text-blue-400 font-semibold mb-2">🏭 Manufacturer(s) ({data.manufacturers.length})</p>
                            <div className="space-y-2">
                                {data.manufacturers.map((m, idx) => (
                                    <div key={idx} className="text-xs bg-blue-900/20 rounded px-2 py-1.5">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="shrink-0 px-1.5 py-0.5 rounded bg-blue-800/50 text-blue-300 font-mono text-[10px]">{idx + 1}</span>
                                            <span className="text-white font-semibold">{m.name || 'Unknown'}</span>
                                            {m.partNumber && <span className="text-blue-300 text-[10px]">PN: {m.partNumber}</span>}
                                            {m.confidence !== undefined && m.confidence !== null && <span className="ml-auto text-blue-200 text-[10px] font-mono">{m.confidence}%</span>}
                                        </div>
                                        <a href={m.url} target="_blank" rel="noopener noreferrer"
                                            className="text-cyan-300 hover:text-cyan-200 break-all flex items-center gap-1 text-[11px] ml-6">
                                            {m.url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : data.manufacturer_url ? (
                        <div className="bg-slate-800 rounded-lg p-3 border-l-2 border-blue-500">
                            <p className="text-xs text-blue-400 font-semibold mb-1">🏭 Manufacturer URL</p>
                            <a href={data.manufacturer_url} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-cyan-300 hover:text-cyan-200 break-all flex items-center gap-1">
                                {data.manufacturer_url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                        </div>
                    ) : null}

                    {/* Vendors (with name + URL) */}
                    {data.vendors && data.vendors.length > 0 ? (
                        <div className="bg-slate-800 rounded-lg p-3 border-l-2 border-orange-500">
                            <p className="text-xs text-orange-400 font-semibold mb-2">🏪 Vendor(s) ({data.vendors.length})</p>
                            <div className="space-y-2">
                                {data.vendors.map((v, idx) => (
                                    <div key={idx} className="text-xs bg-orange-900/20 rounded px-2 py-1.5">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="shrink-0 px-1.5 py-0.5 rounded bg-orange-800/50 text-orange-300 font-mono text-[10px]">{idx + 1}</span>
                                            <span className="text-white font-semibold">{v.name || 'Unknown'}</span>
                                            {v.partNumber && <span className="text-orange-300 text-[10px]">PN: {v.partNumber}</span>}
                                            {v.confidence !== undefined && v.confidence !== null && <span className="ml-auto text-orange-200 text-[10px] font-mono">{v.confidence}%</span>}
                                        </div>
                                        <a href={v.url} target="_blank" rel="noopener noreferrer"
                                            className="text-cyan-300 hover:text-cyan-200 break-all flex items-center gap-1 text-[11px] ml-6">
                                            {v.url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : data.vendor_urls && data.vendor_urls.length > 0 ? (
                        <div className="bg-slate-800 rounded-lg p-3 border-l-2 border-orange-500">
                            <p className="text-xs text-orange-400 font-semibold mb-2">🏪 Vendor URLs ({data.vendor_urls.length})</p>
                            <div className="space-y-1.5">
                                {data.vendor_urls.map((url, idx) => (
                                    <div key={idx} className="text-xs bg-orange-900/20 rounded px-2 py-1.5">
                                        <a href={url} target="_blank" rel="noopener noreferrer"
                                            className="text-cyan-300 hover:text-cyan-200 break-all flex items-center gap-1">
                                            {url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                        </a>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {/* Serper Search Results (full details) */}
                    {data.searchResults && data.searchResults.length > 0 && (
                        <div className="bg-slate-800 rounded-lg p-3">
                            <p className="text-xs text-cyan-400 font-semibold mb-2">🔍 Serper Search Results ({data.searchResults.length})</p>
                            <div className="space-y-2 max-h-60 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 #1e293b' }}>
                                {data.searchResults.map((r, idx) => (
                                    <div key={idx} className="bg-slate-700/50 rounded-lg p-2.5 text-xs space-y-1">
                                        <div className="flex items-start gap-2">
                                            <span className="text-amber-400 font-bold shrink-0">#{r.position}</span>
                                            <span className="text-white font-semibold">{r.title}</span>
                                            {r.searchSource && (
                                                <span className={`ml-auto shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${r.searchSource === 'partNumber' ? 'bg-purple-900/40 text-purple-300' : 'bg-teal-900/40 text-teal-300'}`}>
                                                    {r.searchSource === 'partNumber' ? 'PN' : 'DESC'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="break-all">
                                            <span className="text-slate-500">URL: </span>
                                            <a href={r.link} target="_blank" rel="noopener noreferrer"
                                                className="text-cyan-300 hover:text-cyan-200 underline">{r.link}</a>
                                        </div>
                                        {r.snippet && (
                                            <div className="text-slate-400 italic text-[11px] leading-relaxed">
                                                {r.snippet}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        if (data.type === 'scraped_content') {
            const currentContent = editedPrompt !== null ? editedPrompt : (data.preview || '');
            const alphanumericCount = (currentContent.match(/[A-Za-z0-9]/g) || []).length;
            const tokenEstimate = Math.ceil(currentContent.length / 3);
            return (
                <div className="space-y-3">
                    <div className="bg-slate-800 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-2">
                            <p className="text-xs text-slate-400">Source URL</p>
                        </div>
                        <a href={data.url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-cyan-300 hover:text-cyan-200 break-all flex items-center gap-1">
                            {data.url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                    </div>

                    {/* Stats bar */}
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                        <span className="px-2 py-1 rounded bg-slate-800 text-slate-300">
                            Total: {currentContent.length.toLocaleString()} chars
                        </span>
                        <span className="px-2 py-1 rounded bg-emerald-900/40 text-emerald-300">
                            Alphanumeric: {alphanumericCount.toLocaleString()} chars
                        </span>
                        <span className="px-2 py-1 rounded bg-violet-900/40 text-violet-300">
                            ~{tokenEstimate.toLocaleString()} tokens (chars÷3)
                        </span>
                    </div>

                    {/* Editable scraped content */}
                    <div className="bg-slate-800 rounded-lg p-3">
                        <p className="text-xs text-amber-400 font-semibold mb-2 flex items-center justify-between">
                            <span>📄 Scraped Content (editable — this will be sent to AI)</span>
                            <span className="text-slate-400 font-normal">Edit below before proceeding</span>
                        </p>
                        <textarea
                            value={currentContent}
                            onChange={(e) => setEditedPrompt(e.target.value)}
                            className="w-full h-72 text-xs text-emerald-200 bg-slate-900 border border-slate-700/50 rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-mono leading-relaxed resize-y"
                            style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 #1e293b' }}
                        />
                    </div>
                </div>
            );
        }

        if (data.type === 'spec_extraction') {
            return (
                <div className="space-y-3">
                    <div className="flex items-center gap-3 text-xs flex-wrap">
                        <span className="px-2 py-1 rounded bg-orange-900/40 text-orange-300">
                            Original: {data.originalLength?.toLocaleString()} chars
                        </span>
                        <span className="px-2 py-1 rounded bg-emerald-900/40 text-emerald-300">
                            Cleaned: {data.cleanedLength?.toLocaleString()} chars
                        </span>
                        <span className="px-2 py-1 rounded bg-cyan-900/40 text-cyan-300">
                            Specs: {data.specLength?.toLocaleString()} chars
                        </span>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-3">
                        <p className="text-xs text-cyan-400 font-semibold mb-2">🧹 AI-Extracted Specifications</p>
                        <pre className="text-xs text-slate-300 whitespace-pre-wrap max-h-80 overflow-y-auto font-mono leading-relaxed"
                            style={{ scrollbarWidth: 'thin' }}>
                            {data.preview}
                        </pre>
                    </div>
                </div>
            );
        }

        if (data.type === 'classification_prompt_preview') {
            return (
                <div className="space-y-3">
                    {/* Stats bar */}
                    <div className="flex items-center gap-3 text-xs flex-wrap">
                        <span className="px-2 py-1 rounded bg-indigo-900/40 text-indigo-300">
                            🏭 AI Link Ranking — manufacturer & vendor + confidence
                        </span>
                        <span className="px-2 py-1 rounded bg-sky-900/40 text-sky-300">
                            {data.sourceCount} source(s)
                        </span>
                        <span className="px-2 py-1 rounded bg-amber-900/40 text-amber-300">
                            Prompt: {data.promptLength?.toLocaleString()} chars
                        </span>
                        <span className="px-2 py-1 rounded bg-violet-900/40 text-violet-300">
                            Model: {data.model}
                        </span>
                    </div>

                    {/* Source URLs list */}
                    {data.sources?.length > 0 && (
                        <div className="bg-slate-800 rounded-lg p-3">
                            <p className="text-xs text-sky-400 font-semibold mb-2">🔗 URLs to Classify</p>
                            <div className="space-y-1.5 max-h-40 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 #1e293b' }}>
                                {data.sources.map((s, idx) => (
                                    <div key={idx} className="flex items-start gap-2 text-xs">
                                        <span className="shrink-0 px-1.5 py-0.5 rounded bg-slate-700 text-slate-400 font-mono">{idx + 1}</span>
                                        <div className="min-w-0">
                                            <div className="text-slate-200 font-medium truncate">{s.title}</div>
                                            <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300 truncate block text-[11px]">{s.url}</a>
                                            <span className="text-slate-500 text-[11px]">{s.domain}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Full prompt (editable) */}
                    <div className="bg-slate-800 rounded-lg p-3">
                        <p className="text-xs text-amber-400 font-semibold mb-2 flex items-center justify-between">
                            <span>🤖 Classification Prompt (sent to Gemini)</span>
                            <span className="text-slate-400 font-normal">Review before sending</span>
                        </p>
                        <pre className="text-xs text-emerald-200 bg-slate-900 border border-slate-700/50 rounded-lg p-3 whitespace-pre-wrap max-h-72 overflow-y-auto font-mono leading-relaxed"
                            style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 #1e293b' }}>
                            {data.fullPrompt}
                        </pre>
                    </div>
                </div>
            );
        }

        if (data.type === 'prompt_preview') {
            return (
                <div className="space-y-3">
                    {/* Stats bar */}
                    <div className="flex items-center gap-3 text-xs">
                        <span className="px-2 py-1 rounded bg-violet-900/40 text-violet-300">
                            {data.attributeLines?.length} attribute(s)
                        </span>
                        <span className="px-2 py-1 rounded bg-sky-900/40 text-sky-300">
                            {data.originalContentLength?.toLocaleString()} → {data.structuredContentLength?.toLocaleString()} chars (structured)
                        </span>
                        <span className="px-2 py-1 rounded bg-amber-900/40 text-amber-300">
                            Prompt: {data.promptLength?.toLocaleString()} chars
                        </span>
                    </div>

                    {/* Attributes list */}
                    <div className="bg-slate-800 rounded-lg p-3">
                        <p className="text-xs text-violet-400 font-semibold mb-2">📋 Attributes to Extract</p>
                        <div className="flex flex-wrap gap-1.5">
                            {data.attributeLines?.map((attr, idx) => (
                                <span key={idx} className="px-2 py-0.5 rounded bg-violet-900/30 text-violet-300 text-xs border border-violet-800/50">
                                    {attr}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Structured content */}
                    <div className="bg-slate-800 rounded-lg p-3">
                        <p className="text-xs text-sky-400 font-semibold mb-2">📄 Structured Content (sent to AI)</p>
                        <pre className="text-xs text-slate-300 whitespace-pre-wrap max-h-48 overflow-y-auto font-mono leading-relaxed"
                            style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 #1e293b' }}>
                            {data.structuredContent?.substring(0, 4000)}
                            {data.structuredContent?.length > 4000 && '\n\n... [truncated for display]'}
                        </pre>
                    </div>

                    {/* Full prompt text */}
                    <div className="bg-slate-800 rounded-lg p-3">
                        <p className="text-xs text-amber-400 font-semibold mb-2 flex items-center justify-between">
                            <span>🤖 Edited Gemini Prompt</span>
                            <span className="text-slate-400 font-normal">Edit below before sending</span>
                        </p>
                        <textarea
                            value={editedPrompt !== null ? editedPrompt : data.fullPrompt}
                            onChange={(e) => setEditedPrompt(e.target.value)}
                            className="w-full h-64 text-xs text-emerald-200 bg-slate-900 border border-slate-700/50 rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-mono leading-relaxed resize-y"
                            style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 #1e293b' }}
                        />
                    </div>
                </div>
            );
        }

        if (data.type === 'extracted_attributes') {
            return (
                <div className="space-y-3">
                    <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-3">
                        <p className="text-xs text-emerald-400 mb-1">Extracted {data.attributeCount} attribute(s)</p>
                    </div>
                    <div className="bg-slate-800 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-2">Attributes</p>
                        <div className="space-y-1 max-h-64 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                            {data.attributes && Object.entries(data.attributes).map(([key, val]) => {
                                const display = (val && typeof val === 'object') ? JSON.stringify(val) : val;
                                return (
                                    <div key={key} className="flex justify-between text-xs py-1 border-b border-slate-700/50 last:border-0">
                                        <span className="text-sky-400 font-medium">{key}</span>
                                        <span className="text-slate-300 font-mono text-right max-w-[60%] truncate" title={String(display ?? '')}>
                                            {display || <span className="text-slate-600">null</span>}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {data.mapped && Object.keys(data.mapped).length > 0 && (
                        <div className="bg-slate-800 rounded-lg p-3">
                            <p className="text-xs text-slate-400 mb-2">Mapped Schema (PROPERTY → VALUE)</p>
                            <div className="space-y-1 max-h-40 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                                {Object.entries(data.mapped).filter(([k]) => k.startsWith('PROPERTY')).map(([key, val]) => {
                                    const num = key.replace('PROPERTY_', '');
                                    const valueKey = `VALUE_${num}`;
                                    const propDisplay = (val && typeof val === 'object') ? JSON.stringify(val) : val;
                                    const valDisplay = data.mapped[valueKey];
                                    const valStr = (valDisplay && typeof valDisplay === 'object') ? JSON.stringify(valDisplay) : valDisplay;
                                    return (
                                        <div key={key} className="flex text-xs py-0.5">
                                            <span className="text-violet-400 w-32">{String(propDisplay ?? '')}</span>
                                            <span className="text-slate-500 mx-2">→</span>
                                            <span className="text-slate-300">{String(valStr ?? '') || '—'}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        if (data.type === 'manufacturer_validation') {
            const actionColors = {
                REROUTE: { bg: 'bg-emerald-900/40', text: 'text-emerald-300', label: '🔄 REROUTE to manufacturer URL' },
                KEEP: { bg: 'bg-blue-900/40', text: 'text-blue-300', label: '✅ KEEP current URL (already manufacturer)' },
                NO_MATCH: { bg: 'bg-amber-900/40', text: 'text-amber-300', label: 'ℹ️ NO MATCH — using vendor URL' },
            };
            const actionInfo = actionColors[data.action] || { bg: 'bg-slate-800', text: 'text-slate-300', label: data.action };

            return (
                <div className="space-y-3">
                    {/* Stats bar */}
                    <div className="flex items-center gap-3 text-xs flex-wrap">
                        <span className="px-2 py-1 rounded bg-indigo-900/40 text-indigo-300">
                            🏭 Manufacturer Validation
                        </span>
                        <span className={`px-2 py-1 rounded ${actionInfo.bg} ${actionInfo.text}`}>
                            {actionInfo.label}
                        </span>
                        {data.source && (
                            <span className="px-2 py-1 rounded bg-violet-900/40 text-violet-300">
                                Source: {data.source}
                            </span>
                        )}
                    </div>

                    {/* Manufacturer Info */}
                    <div className="bg-slate-800 rounded-lg p-3 space-y-2">
                        <p className="text-xs text-sky-400 font-semibold mb-2">🏭 Manufacturer Details</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                                <span className="text-slate-500">Manufacturer:</span>
                                <span className="ml-2 text-white font-medium">{data.manufacturerName || '—'}</span>
                            </div>
                            <div>
                                <span className="text-slate-500">Part Number:</span>
                                <span className="ml-2 text-white font-mono">{data.partNumber || '—'}</span>
                            </div>
                            <div className="col-span-2">
                                <span className="text-slate-500">Current Scrape URL:</span>
                                <a href={data.scrapableUrl} target="_blank" rel="noopener noreferrer"
                                    className="ml-2 text-sky-400 hover:text-sky-300 break-all text-[11px]">
                                    {data.scrapableUrl}
                                </a>
                            </div>
                            {data.resultUrl && data.resultUrl !== data.scrapableUrl && (
                                <div className="col-span-2">
                                    <span className="text-slate-500">Manufacturer URL (reroute):</span>
                                    <a href={data.resultUrl} target="_blank" rel="noopener noreferrer"
                                        className="ml-2 text-emerald-400 hover:text-emerald-300 break-all text-[11px]">
                                        {data.resultUrl}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Validation Steps */}
                    {data.steps && data.steps.length > 0 && (
                        <div className="bg-slate-800 rounded-lg p-3">
                            <p className="text-xs text-amber-400 font-semibold mb-2">📋 Validation Steps</p>
                            <div className="space-y-1.5">
                                {data.steps.map((step, idx) => {
                                    const stepColor = step.result === 'match' ? 'text-emerald-400'
                                        : step.result === 'no_match' ? 'text-slate-400'
                                            : step.result === 'error' ? 'text-red-400' : 'text-sky-400';
                                    return (
                                        <div key={idx} className="flex items-start gap-2 text-xs">
                                            <span className={`shrink-0 font-mono ${stepColor}`}>
                                                {step.result === 'match' ? '✅' : step.result === 'error' ? '❌' : '➖'}
                                            </span>
                                            <span className="text-slate-300">{step.step || step.action || `Step ${idx + 1}`}</span>
                                            {step.detail && <span className="text-slate-500 ml-auto">{step.detail}</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Domain/DB Match info */}
                    <div className="bg-slate-800 rounded-lg p-3 flex gap-4 text-xs">
                        <div className="flex items-center gap-1.5">
                            <span className={data.domainMatch ? 'text-emerald-400' : 'text-slate-500'}>
                                {data.domainMatch ? '✅' : '❌'}
                            </span>
                            <span className="text-slate-400">Domain Match</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className={data.dbMatch ? 'text-emerald-400' : 'text-slate-500'}>
                                {data.dbMatch ? '✅' : '❌'}
                            </span>
                            <span className="text-slate-400">DB Match</span>
                        </div>
                        {data.dataSource && (
                            <div className="flex items-center gap-1.5 ml-auto">
                                <span className="text-slate-500">Data Source:</span>
                                <span className="text-white font-medium">{data.dataSource}</span>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return <p className="text-sm text-slate-400">No preview available</p>;
    };

    return (
        <>
        <div className="max-w-6xl mx-auto">
            {/* <div className="mb-8 card bg-gradient-to-r from-slate-900 to-slate-800 text-white border-0 shadow-lg">
                <h1 className="text-3xl font-extrabold mb-2">Upload Excel Files</h1>
                <p className="text-slate-200">
                    Upload Input + INC files, then start the enrichment pipeline with manual step-by-step verification.
                </p>
            </div> */}

            {/* ── Rescrape Banner ─────────────────────────────── */}
            {rescrapeData && rescrapeData.length > 0 && !enrichmentRunning && (
                <div className="mb-6 card bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200 shadow-md">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                                <RefreshCcw className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-orange-900">
                                    Rescrape {rescrapeData.length} item{rescrapeData.length > 1 ? 's' : ''}
                                </h3>
                                <p className="text-xs text-orange-700 mt-0.5">
                                    These items were selected from the Results page for re-processing
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setRescrapeData(null)}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-orange-700 hover:bg-orange-100 transition-colors"
                            >
                                Dismiss
                            </button>
                            <button
                                onClick={startRescrapeProcess}
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold
                                           bg-gradient-to-r from-orange-500 to-amber-500 text-white
                                           hover:from-orange-600 hover:to-amber-600 shadow-md hover:shadow-lg
                                           transition-all duration-200"
                            >
                                <RefreshCcw className="w-4 h-4" />
                                Start Rescrape
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* ═══ Processing Mode ═════════════════════════════════════ */}
            <div className="card mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-slate-900">Processing Mode</h2>
                        <p className="text-sm text-slate-500 mt-0.5">
                            {processingMode === 'cleansed'
                                ? 'Input + INC → DB check → AI extraction. Search, classification and scraping are skipped.'
                                : 'The full pipeline: DB check, search, classification, scraping, then AI extraction.'}
                        </p>
                    </div>
                    <div
                        role="radiogroup"
                        aria-label="Processing mode"
                        className="inline-flex shrink-0 rounded-xl bg-slate-100 p-1"
                    >
                        {[
                            { id: 'cleansed', label: 'Cleansed Data', Icon: Zap },
                            { id: 'enrichment', label: 'Enrichment Data', Icon: Sparkles },
                        ].map(({ id, label, Icon }) => {
                            const active = processingMode === id;
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    role="radio"
                                    aria-checked={active}
                                    disabled={pipelineState === 'running' || pipelineState === 'paused'}
                                    onClick={() => setProcessingMode(id)}
                                    className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                        active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                                    }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Input Data Upload */}
                <div className="card">
                    <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
                        <FileSpreadsheet className="w-5 h-5 mr-2 text-primary-600" />
                        Input Data Excel
                        <span className="ml-2 text-red-500">*</span>
                    </h2>
                    <div
                        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${dragActive ? 'border-primary-500 bg-primary-50' : 'border-slate-300 hover:border-primary-400 bg-slate-50/50'}`}
                        onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag}
                        onDrop={(e) => handleDrop(e, 'input')}
                    >
                        <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                        {files.input ? (
                            <div className="flex items-center justify-center text-green-600">
                                <CheckCircle className="w-5 h-5 mr-2" />
                                <span className="font-medium">{files.input.name}</span>
                            </div>
                        ) : (
                            <>
                                <p className="text-slate-600 mb-2">Drag and drop or click to upload</p>
                                <p className="text-sm text-slate-400">Supports .xls and .xlsx files</p>
                            </>
                        )}
                        <input type="file" accept=".xls,.xlsx" onChange={(e) => handleFileChange(e.target.files[0], 'input')} className="hidden" id="input-file" />
                        <label htmlFor="input-file" className="mt-4 inline-block btn-secondary cursor-pointer">Choose File</label>
                    </div>
                    <div className="mt-2 flex justify-end">
                        <button type="button" onClick={downloadInputSample} className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700">
                            <Download className="w-4 h-4" />
                            Sample sheet
                        </button>
                    </div>
                </div>

                {/* INC Data Upload */}
                <div className="card">
                    <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
                        <FileSpreadsheet className="w-5 h-5 mr-2 text-primary-600" />
                        INC Data Excel
                        <span className="ml-2 text-red-500">*</span>
                    </h2>
                    <div
                        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${dragActive ? 'border-primary-500 bg-primary-50' : 'border-slate-300 hover:border-primary-400 bg-slate-50/50'}`}
                        onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag}
                        onDrop={(e) => handleDrop(e, 'inc')}
                    >
                        <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                        {files.inc ? (
                            <div className="flex items-center justify-center text-green-600">
                                <CheckCircle className="w-5 h-5 mr-2" />
                                <span className="font-medium">{files.inc.name}</span>
                            </div>
                        ) : (
                            <>
                                <p className="text-slate-600 mb-2">Drag and drop or click to upload</p>
                                <p className="text-sm text-slate-400">Supports .xls and .xlsx files</p>
                            </>
                        )}
                        <input type="file" accept=".xls,.xlsx" onChange={(e) => handleFileChange(e.target.files[0], 'inc')} className="hidden" id="inc-file" />
                        <label htmlFor="inc-file" className="mt-4 inline-block btn-secondary cursor-pointer">Choose File</label>
                    </div>
                    <div className="mt-2 flex justify-end">
                        <button type="button" onClick={downloadIncSample} className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700">
                            <Download className="w-4 h-4" />
                            Sample sheet
                        </button>
                    </div>
                </div>
            </div>

            {/* ═══ Processing Control Bar ═══════════════════════════════ */}
            {pipelineState === 'idle' ? (
                /* Start Button — only when idle */
                <div className="card bg-gradient-to-r from-primary-50 to-cyan-50 border border-primary-200">
                    <button
                        onClick={startEnrichmentProcess}
                        disabled={!files.input || !files.inc}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center w-full"
                        style={{ background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', border: 'none', fontSize: '1rem', padding: '14px 24px' }}
                    >
                        <Play className="w-5 h-5 mr-2" />
                        {processingMode === 'cleansed' ? 'Start Cleansed Processing' : 'Start Enrichment Processing'}
                    </button>
                </div>
            ) : (pipelineState === 'running' || pipelineState === 'paused') ? (
                /* Active Processing Control Bar */
                <div className="card" style={{ background: '#0f172a', border: '2px solid ' + (pipelineState === 'paused' ? '#f59e0b' : '#10b981') }}>
                    {/* Status + Progress */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            {pipelineState === 'running' ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
                                    <span className="text-sm font-bold text-emerald-400">Processing</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Pause className="w-4 h-4 text-amber-400" />
                                    <span className="text-sm font-bold text-amber-400">Paused</span>
                                </div>
                            )}
                            <span className="text-sm text-slate-400">
                                Record {enrichmentStats.current} of {enrichmentStats.total}
                            </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                            <span className="text-emerald-400 font-mono">✓ {enrichmentStats.completed}</span>
                            {enrichmentStats.failed > 0 && <span className="text-red-400 font-mono">✗ {enrichmentStats.failed}</span>}
                            <span className="text-slate-500 font-mono">
                                {Math.round(((enrichmentStats.completed + enrichmentStats.failed) / Math.max(enrichmentStats.total, 1)) * 100)}%
                            </span>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-slate-800 rounded-full h-2 mb-4">
                        <div className="h-2 rounded-full transition-all duration-500 ease-out"
                            style={{
                                width: `${((enrichmentStats.completed + enrichmentStats.failed) / Math.max(enrichmentStats.total, 1)) * 100}%`,
                                background: pipelineState === 'paused'
                                    ? 'linear-gradient(90deg, #f59e0b, #eab308)'
                                    : 'linear-gradient(90deg, #ea580c, #f59e0b)',
                            }}
                        />
                    </div>

                    {/* Control Buttons */}
                    <div className="flex items-center gap-3">
                        {pipelineState === 'running' ? (
                            <button
                                onClick={handlePause}
                                className="flex-1 py-2.5 px-4 rounded-xl font-semibold text-sm text-white
                                           bg-gradient-to-r from-amber-500 to-amber-600
                                           hover:from-amber-600 hover:to-amber-700
                                           transition-all duration-200 flex items-center justify-center gap-2"
                            >
                                <Pause className="w-4 h-4" />
                                Pause
                            </button>
                        ) : (
                            <button
                                onClick={handleResume}
                                className="flex-1 py-2.5 px-4 rounded-xl font-semibold text-sm text-white
                                           bg-gradient-to-r from-emerald-500 to-emerald-600
                                           hover:from-emerald-600 hover:to-emerald-700
                                           transition-all duration-200 flex items-center justify-center gap-2"
                            >
                                <PlayCircle className="w-4 h-4" />
                                Resume
                            </button>
                        )}

                        <button
                            onClick={handleSkipCurrent}
                            className="py-2.5 px-4 rounded-xl font-semibold text-sm text-slate-300
                                       bg-slate-700 hover:bg-slate-600
                                       transition-all duration-200 flex items-center justify-center gap-2"
                        >
                            <SkipForward className="w-4 h-4" />
                            Skip Record
                        </button>

                        <button
                            onClick={handleStop}
                            className="py-2.5 px-4 rounded-xl font-semibold text-sm text-red-400
                                       bg-red-900/30 border border-red-800/50 hover:bg-red-900/50
                                       transition-all duration-200 flex items-center justify-center gap-2"
                        >
                            <StopCircle className="w-4 h-4" />
                            Stop
                        </button>
                    </div>
                </div>
            ) : (pipelineState === 'stopped' || pipelineState === 'completed') ? (
                /* Stopped / Completed Summary Bar */
                <div className="card" style={{
                    background: '#0f172a',
                    border: '2px solid ' + (pipelineState === 'completed' ? '#10b981' : '#f59e0b')
                }}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            {pipelineState === 'completed' ? (
                                <CheckCircle className="w-5 h-5 text-emerald-400" />
                            ) : (
                                <StopCircle className="w-5 h-5 text-amber-400" />
                            )}
                            <span className={`text-sm font-bold ${pipelineState === 'completed' ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {pipelineState === 'completed' ? 'Pipeline Complete!' : 'Pipeline Stopped'}
                            </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                            <span className="text-slate-400">Total: {enrichmentStats.total}</span>
                            <span className="text-emerald-400">✓ {enrichmentStats.completed}</span>
                            {enrichmentStats.failed > 0 && <span className="text-red-400">✗ {enrichmentStats.failed}</span>}
                            {enrichmentStats.total - enrichmentStats.completed - enrichmentStats.failed > 0 && (
                                <span className="text-amber-400">⏳ {enrichmentStats.total - enrichmentStats.completed - enrichmentStats.failed} remaining</span>
                            )}
                        </div>
                    </div>

                    {/* Full Progress Bar */}
                    <div className="w-full bg-slate-800 rounded-full h-2 mb-4">
                        <div className="h-2 rounded-full transition-all duration-300"
                            style={{
                                width: `${((enrichmentStats.completed + enrichmentStats.failed) / Math.max(enrichmentStats.total, 1)) * 100}%`,
                                background: pipelineState === 'completed' ? '#10b981' : 'linear-gradient(90deg, #10b981, #f59e0b)',
                            }}
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/results', { state: { reload: Date.now() } })}
                            className="flex-1 py-2.5 px-4 rounded-xl font-semibold text-sm text-white
                                       bg-gradient-to-r from-sky-500 to-cyan-500
                                       hover:from-sky-600 hover:to-cyan-600
                                       transition-all duration-200 flex items-center justify-center gap-2"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Go to Results
                        </button>
                        <button
                            onClick={() => { setPipelineState('idle'); setEnrichmentLogs([]); setEnrichmentStats({ total: 0, completed: 0, failed: 0, current: 0 }); }}
                            className="py-2.5 px-4 rounded-xl font-semibold text-sm text-slate-300
                                       bg-slate-700 hover:bg-slate-600
                                       transition-all duration-200 flex items-center justify-center gap-2"
                        >
                            <RefreshCcw className="w-4 h-4" />
                            New Batch
                        </button>
                    </div>
                </div>
            ) : null}

            {/* ═══ Step Review Panel ═══════════════════════════════════════ */}
            {stepReview && (
                <div className="card mt-6 animate-fade-in" style={{ background: '#0f172a', border: '2px solid #10b981' }}>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm">
                                {stepReview.stepNumber}
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-white">{stepReview.stepName}</h3>
                                <p className="text-xs text-slate-400">
                                    Row {stepReview.row.rowNum} — {stepReview.row.identifier}
                                </p>
                            </div>
                        </div>

                        {/* Step indicators */}
                        <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                                <div key={n} className="flex items-center">
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold
                                        ${n < stepReview.stepNumber ? 'bg-emerald-500 text-white' :
                                            n === stepReview.stepNumber ? 'bg-cyan-500 text-white ring-2 ring-cyan-400/50' :
                                                'bg-slate-700 text-slate-500'}`}>
                                        {n < stepReview.stepNumber ? '✓' : n}
                                    </div>
                                    {n < 9 && <ChevronRight className={`w-2.5 h-2.5 mx-0.5 ${n < stepReview.stepNumber ? 'text-emerald-500' : 'text-slate-700'}`} />}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Step Data */}
                    <div className="mb-4">
                        {renderStepData()}
                    </div>

                    {/* Approval Buttons */}
                    <div className="flex items-center gap-3 pt-3 border-t border-slate-700">
                        <button
                            onClick={handleApprove}
                            className="flex-1 py-2.5 px-4 rounded-xl font-semibold text-sm text-white
                                       bg-gradient-to-r from-emerald-500 to-emerald-600
                                       hover:from-emerald-600 hover:to-emerald-700
                                       transition-all duration-200 flex items-center justify-center gap-2"
                        >
                            <CheckCircle className="w-4 h-4" />
                            Approve & Continue
                        </button>
                        <button
                            onClick={handleSkipRow}
                            className="py-2.5 px-4 rounded-xl font-semibold text-sm text-slate-300
                                       bg-slate-700 hover:bg-slate-600
                                       transition-all duration-200 flex items-center justify-center gap-2"
                        >
                            <X className="w-4 h-4" />
                            Skip Row
                        </button>
                    </div>
                </div>
            )}

            {/* ═══ Console Log ═════════════════════════════════════════════ */}
            {enrichmentLogs.length > 0 && (
                <div className="card mt-6" style={{ background: '#0f172a', border: '1px solid #1e293b' }}>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-bold text-emerald-400 flex items-center">
                            <Terminal className="w-4 h-4 mr-2" />
                            Pipeline Console
                        </h3>
                        <div className="flex items-center gap-3 text-xs">
                            {enrichmentStats.total > 0 && (
                                <>
                                    <span className="text-slate-400">Total: {enrichmentStats.total}</span>
                                    <span className="text-emerald-400">✓ {enrichmentStats.completed}</span>
                                    {enrichmentStats.failed > 0 && <span className="text-red-400">✗ {enrichmentStats.failed}</span>}
                                    <span className="text-cyan-400">{enrichmentStats.current}/{enrichmentStats.total}</span>
                                </>
                            )}
                            {enrichmentRunning && (
                                <div className="animate-pulse flex items-center text-emerald-400">
                                    <div className="w-2 h-2 rounded-full bg-emerald-400 mr-1"></div>
                                    LIVE
                                </div>
                            )}
                        </div>
                    </div>

                    {enrichmentStats.total > 0 && (
                        <div className="w-full bg-slate-800 rounded-full h-1.5 mb-3">
                            <div className="h-1.5 rounded-full transition-all duration-300"
                                style={{
                                    width: `${((enrichmentStats.completed + enrichmentStats.failed) / enrichmentStats.total) * 100}%`,
                                    background: enrichmentStats.failed > 0 ? 'linear-gradient(90deg, #10b981, #f59e0b)' : '#10b981'
                                }}
                            />
                        </div>
                    )}

                    <div className="font-mono text-xs leading-relaxed overflow-y-auto"
                        style={{ maxHeight: '300px', scrollbarWidth: 'thin', scrollbarColor: '#334155 #0f172a' }}>
                        {enrichmentLogs.map((log, idx) => {
                            const colorMap = {
                                header: 'text-cyan-300 font-bold',
                                step: 'text-sky-300',
                                success: 'text-emerald-400',
                                error: 'text-red-400',
                                info: 'text-slate-300',
                                detail: 'text-slate-500',
                                divider: 'text-slate-700'
                            };
                            return (
                                <div key={idx} className={colorMap[log.type] || 'text-slate-300'}>
                                    <span className="text-slate-600 mr-2">[{log.timestamp}]</span>
                                    {log.message}
                                </div>
                            );
                        })}
                        <div ref={consoleEndRef} />
                    </div>
                </div>
            )}
        </div>

            {/* ═══ Processing Overlay ═══ */}
            <ProcessingOverlay
                visible={pipelineState !== 'idle'}
                pipelineState={pipelineState}
                enrichmentStats={enrichmentStats}
                enrichmentLogs={enrichmentLogs}
                startTime={pipelineStartTimeRef.current}
                currentStepName={currentStepName}
                stepReview={stepReview}
                previewMode={previewMode}
                onPause={handlePause}
                onResume={handleResume}
                onStop={handleStop}
                onSkip={handleSkipCurrent}
                onClose={() => {
                    setPipelineState('idle');
                    setEnrichmentLogs([]);
                    setEnrichmentStats({ total: 0, completed: 0, failed: 0, current: 0 });
                    pipelineStartTimeRef.current = null;
                    setCurrentStepName('');
                    if (pipelineState === 'completed') {
                        navigate('/results');
                    }
                }}
                consoleEndRef={consoleEndRef}
                renderStepData={renderStepData}
                handleApprove={handleApprove}
                handleSkipRow={handleSkipRow}
            />
        </>
    );
}

export default UploadPage;
